import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ThemeProvider from "../theme/TemaMuiProvider";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Popover,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SendIcon from "@mui/icons-material/Send";
import ArchiveIcon from "@mui/icons-material/Archive";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { copiarTexto } from "../utils/clipboard";
import muiTheme from "../theme/muiTheme";

const humanizarTextoTecnico = (valor) => {
  if (!valor) return "—";

  const mapa = {
    rascunho: "Rascunho",
    RASCUNHO: "Rascunho",
    EM_ASSINATURA_FISICA: "Em Assinatura Física",
    PRONTO_PARA_ENVIO: "Pronto Para Envio",
    ENVIADO: "Enviado",
    AGUARDANDO_ASSINATURAS: "Aguardando Assinaturas",
    PARCIALMENTE_ASSINADO: "Parcialmente Assinado",
    ASSINADO: "Assinado",
    ARQUIVADO: "Arquivado",
    CANCELADO: "Cancelado",

    DOCUSIGN: "Digital — DocuSign",
    FISICO: "Físico — PDF + QR Code",
    FISICA: "Físico — PDF + QR Code",

    PDF_ASSINADO_FISICO: "PDF Assinado Físico",
    CONTRATO_FINAL: "Contrato Final",
    MINUTA: "Minuta",
    PROPOSTA: "Proposta Comercial",
    DOCUMENTO: "Documento",

    STATUS_FISICO: "Alteração de Status",
    UPLOAD_DOCUMENTO_FISICO: "Documento Enviado",
    CRIACAO_PROCESSO: "Criação do Processo",
    ASSINATURA_CONCLUIDA: "Assinatura Concluída",
    ARQUIVAMENTO: "Arquivamento",
  };

  let texto = String(valor);

  Object.entries(mapa).forEach(([tecnico, humano]) => {
    texto = texto.replaceAll(tecnico, humano);
  });

  return texto.replaceAll("_", " ");
};

const statusLabel = (status) => {
  return humanizarTextoTecnico(status);
};

const statusColor = (status) => {
  const mapa = {
    rascunho: "warning",
    RASCUNHO: "warning",
    PRONTO_PARA_ENVIO: "info",
    ENVIADO: "info",
    AGUARDANDO_ASSINATURAS: "warning",
    PARCIALMENTE_ASSINADO: "warning",
    ASSINADO: "success",
    ARQUIVADO: "success",
    CANCELADO: "error",
    EM_ASSINATURA_FISICA: "warning",
  };

  return mapa[status] || "default";
};

const modalidadeLabel = (modalidade) => {
  return humanizarTextoTecnico(modalidade || "—");
};

const modalidadeProcesso = (processo) => {
  return (
    processo?.modalidadeAssinatura || processo?.modalidade || processo?.tipoAssinatura || processo?.tipo_assinatura || processo?.modalidade_assinatura || ""
  );
};

const tipoDocumentoLabel = (tipo) => {
  return humanizarTextoTecnico(tipo || "DOCUMENTO");
};

const participantePapelLabel = (papel) => {
  switch (String(papel || "").toUpperCase()) {
    case "LOCATARIO":
      return "Locatário";
    case "FIADOR":
      return "Fiador";
    case "CONJUGE_LOCATARIO":
      return "Cônjuge do Locatário";
    case "CONJUGE_FIADOR":
      return "Cônjuge do Fiador";
    case "ASSINANTE_EMPRESA":
      return "Assinante da Empresa";
    default:
      return humanizarTextoTecnico(papel || "");
  }
};

const participanteStatusLabel = (status) => {
  switch (String(status || "").toUpperCase()) {
    case "PENDENTE":
      return "Pendente";
    case "AGUARDANDO":
      return "Aguardando";
    case "ASSINADO":
      return "Assinado";
    case "RECUSADO":
      return "Recusado";
    default:
      return humanizarTextoTecnico(status || "");
  }
};

const eventoLabel = (tipo) => {
  switch (String(tipo || "").toUpperCase()) {
    case "CRIACAO_PROCESSO":
      return "Criação do Processo";

    case "ALTERACAO_OBSERVACOES":
      return "Alteração de Observações";

    case "ALTERACAO_ASSINANTES":
      return "Alteração de Assinantes";

    case "ENVIO_DOCUSIGN":
      return "Envio Digital — DocuSign";

    case "CONSULTA_STATUS_DOCUSIGN":
      return "Consulta de Status — DocuSign";

    case "DOWNLOAD_PDF_ASSINADO_DOCUSIGN":
      return "Documento Assinado — DocuSign";

    case "ASSINATURA_PARTICIPANTE_CONCLUIDA":
      return "Assinatura Concluída";

    case "STATUS_FISICO":
      return "Alteração de Status";

    case "UPLOAD_DOCUMENTO_FISICO":
      return "Documento Enviado";

    default:
      return humanizarTextoTecnico(tipo || "");
  }
};

const descricaoEventoLabel = (descricao) => {
  return String(descricao || "")
    .replaceAll("LOCATARIO", "Locatário")
    .replaceAll("CONJUGE_LOCATARIO", "Cônjuge do Locatário")
    .replaceAll("CONJUGE_FIADOR", "Cônjuge do Fiador")
    .replaceAll("FIADOR", "Fiador");
};

// O backend já nomeia o arquivo no Content-Disposition (mesmo padrão da impressão:
// Codigo_ApelidoImóvel_PrimeiroNomeLocatária_DataInício_PC) — aqui só se aproveita
// esse nome, com um fallback caso o header venha ausente.
function nomeArquivoRespostaOuPadrao(resposta, padrao) {
  const disposicao = resposta.headers.get("content-disposition") || "";
  const match = disposicao.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (!match) return padrao;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

export default function DocusignAssinaturas() {
  const OPCOES_SIGNATARIO = ["LOCATÁRIO", "FIADOR", "CÔNJUGE DO LOCATÁRIO", "CÔNJUGE DO FIADOR", "REPRESENTANTE LEGAL", "TESTEMUNHA"];
  const OPCOES_CIENCIA = ["NÃO SE APLICA", "SE APLICA"];
  const sincronizandoAutomaticamenteRef = useRef(false);
  const [envelopes, setEnvelopes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(5);
  const [gridMinimizado, setGridMinimizado] = useState(false);
  const [enviandoId, setEnviandoId] = useState(null);

  const [filtroTexto, setFiltroTexto] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("TODOS");
  const [filtroTipo, setFiltroTipo] = useState("TODOS");

  const [filtrosColuna, setFiltrosColuna] = useState({
    DOCUMENTO: "",
    TIPO: [],
    MODALIDADE: [],
    STATUS: [],
    ASSINANTES: "",
    DOCUMENTOS: "",
    CRIADO_EM_DE: "",
    CRIADO_EM_ATE: "",
  });

  const [filtroColunaAberto, setFiltroColunaAberto] = useState(null);
  const [ancoraFiltroColuna, setAncoraFiltroColuna] = useState(null);

  const [colunaOrdenacao, setColunaOrdenacao] = useState("CRIADO_EM");
  const [direcaoOrdenacao, setDirecaoOrdenacao] = useState("DESC");

  const [formNovaAssinaturaAberto, setFormNovaAssinaturaAberto] = useState(false);
  const [modalDetalhes, setModalDetalhes] = useState(false);
  const [assinaturaSelecionada, setAssinaturaSelecionada] = useState(null);
  const [documentosAssinatura, setDocumentosAssinatura] = useState([]);
  const [eventosAssinatura, setEventosAssinatura] = useState([]);
  const [participantesAssinatura, setParticipantesAssinatura] = useState([]);

  const [statusFisico, setStatusFisico] = useState("RASCUNHO");
  const [arquivoFisico, setArquivoFisico] = useState(null);
  const [arquivoDocusignTeste, setArquivoDocusignTeste] = useState(null);

  const [abaDetalhes, setAbaDetalhes] = useState(0);
  const [editandoAssinantes, setEditandoAssinantes] = useState(false);

  const editandoAssinantesRef = useRef(false);

  useEffect(() => {
    editandoAssinantesRef.current = editandoAssinantes;
  }, [editandoAssinantes]);

  const [observacoesDetalhes, setObservacoesDetalhes] = useState("");
  const [editandoObservacoes, setEditandoObservacoes] = useState(false);

  const [novoProcesso, setNovoProcesso] = useState({
    tipoDocumentoOrigem: "PROPOSTA",
    referenciaOrigemId: "",
    tituloContrato: "",
    modalidadeAssinatura: "DOCUSIGN",
    observacoes: "",
  });

  const [snack, setSnack] = useState({
    aberto: false,
    msg: "",
    severity: "success",
  });

  const [documentosAprovados, setDocumentosAprovados] = useState([]);
  const [carregandoDocumentosAprovados, setCarregandoDocumentosAprovados] = useState(false);

  const [modalSelecionarDocumentoAprovado, setModalSelecionarDocumentoAprovado] = useState(false);
  const [buscaDocumentoAprovado, setBuscaDocumentoAprovado] = useState("");
  const [paginaDocumentoAprovado, setPaginaDocumentoAprovado] = useState(0);
  const [porPaginaDocumentoAprovado, setPorPaginaDocumentoAprovado] = useState(10);

  const [alcadaAssinaturaContrato, setAlcadaAssinaturaContrato] = useState(null);

  const [carregandoAlcadaAssinatura, setCarregandoAlcadaAssinatura] = useState(false);

  const [partesContrato, setPartesContrato] = useState({
    locatarios: [],
    fiadores: [],
  });

  const [carregandoPartesContrato, setCarregandoPartesContrato] = useState(false);

  const [conjugesSelecionados, setConjugesSelecionados] = useState({});

  const carregarAlcadaAssinatura = useCallback(async (tipoDocumentoOrigem) => {
    const token = localStorage.getItem("token");

    if (!token) {
      setAlcadaAssinaturaContrato(null);
      return;
    }

    const processoAlcada = tipoDocumentoOrigem === "CONTRATO" ? "CONTRATO" : "PROPOSTA_COMERCIAL";

    try {
      setCarregandoAlcadaAssinatura(true);

      const response = await fetch(`/api/alcadas-assinaturas/processo/${processoAlcada}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 404) {
        setAlcadaAssinaturaContrato(null);
        return;
      }

      if (!response.ok) {
        throw new Error(data.erro || "Erro ao carregar a Alçada de Assinaturas.");
      }

      setAlcadaAssinaturaContrato({
        ...data,
        assinantes: Array.isArray(data?.assinantes)
          ? data.assinantes.map((assinante) => {
              const identificacaoOriginal = String(assinante.identificacao || "")
                .trim()
                .toUpperCase();

              const identificacaoRemovida = ["TESTEMUNHA INTERNA", "REMOVER"].includes(identificacaoOriginal);

              return {
                ...assinante,
                identificacaoExibicao: identificacaoRemovida ? "" : assinante.identificacao || "",
                ordemAssinatura: 1,
                signatario: identificacaoRemovida ? "REPRESENTANTE LEGAL" : assinante.identificacao || "REPRESENTANTE LEGAL",
                ciencia: "NÃO SE APLICA",
                descricaoCiencia: "",
              };
            })
          : [],
      });
    } catch (error) {
      console.error("Erro ao carregar Alçada de Assinaturas:", error);

      setAlcadaAssinaturaContrato(null);

      setSnack({
        aberto: true,
        msg: error.message || "Não foi possível carregar os assinantes da empresa.",
        severity: "error",
      });
    } finally {
      setCarregandoAlcadaAssinatura(false);
    }
  }, []);

  useEffect(() => {
    if (!formNovaAssinaturaAberto || !novoProcesso.tipoDocumentoOrigem) {
      setAlcadaAssinaturaContrato(null);
      return;
    }

    carregarAlcadaAssinatura(novoProcesso.tipoDocumentoOrigem);
  }, [formNovaAssinaturaAberto, novoProcesso.tipoDocumentoOrigem, carregarAlcadaAssinatura]);

  useEffect(() => {
    if (!formNovaAssinaturaAberto || !novoProcesso.tipoDocumentoOrigem || !novoProcesso.referenciaOrigemId) {
      setPartesContrato({
        locatarios: [],
        fiadores: [],
      });

      setConjugesSelecionados({});
      setCarregandoPartesContrato(false);
      return;
    }

    let cancelado = false;

    const carregarPartesAssinatura = async () => {
      const tipoEhContrato = novoProcesso.tipoDocumentoOrigem === "CONTRATO";

      const rotaOrigem = tipoEhContrato ? "contrato" : "proposta";

      try {
        setCarregandoPartesContrato(true);

        setPartesContrato({
          locatarios: [],
          fiadores: [],
        });

        setConjugesSelecionados({});

        const response = await fetch(`/api/assinatura-final/${rotaOrigem}/${novoProcesso.referenciaOrigemId}/partes-assinatura`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.erro || "Erro ao carregar as partes para assinatura.");
        }

        if (cancelado) {
          return;
        }

        setPartesContrato({
          locatarios: Array.isArray(data?.data?.locatarios)
            ? data.data.locatarios.map((pessoa) => ({
                ...pessoa,
                ordemAssinatura: 1,
                signatario: "LOCATÁRIO",
                ciencia: "NÃO SE APLICA",
                descricaoCiencia: "",
                conjuge: pessoa.conjuge
                  ? {
                      ...pessoa.conjuge,
                      ordemAssinatura: 1,
                      signatario: "CÔNJUGE DO LOCATÁRIO",
                      ciencia: "NÃO SE APLICA",
                      descricaoCiencia: "",
                    }
                  : null,
              }))
            : [],

          fiadores: Array.isArray(data?.data?.fiadores)
            ? data.data.fiadores.map((pessoa) => ({
                ...pessoa,
                ordemAssinatura: 1,
                signatario: "FIADOR",
                ciencia: "NÃO SE APLICA",
                descricaoCiencia: "",
                conjuge: pessoa.conjuge
                  ? {
                      ...pessoa.conjuge,
                      ordemAssinatura: 1,
                      signatario: "CÔNJUGE DO FIADOR",
                      ciencia: "NÃO SE APLICA",
                      descricaoCiencia: "",
                    }
                  : null,
              }))
            : [],
        });
      } catch (error) {
        console.error("Erro ao carregar partes para assinatura:", error);

        if (!cancelado) {
          setPartesContrato({
            locatarios: [],
            fiadores: [],
          });

          setConjugesSelecionados({});

          setSnack({
            aberto: true,
            msg: error.message || "Não foi possível carregar locatário, fiador e cônjuge.",
            severity: "error",
          });
        }
      } finally {
        if (!cancelado) {
          setCarregandoPartesContrato(false);
        }
      }
    };

    carregarPartesAssinatura();

    return () => {
      cancelado = true;
    };
  }, [formNovaAssinaturaAberto, novoProcesso.tipoDocumentoOrigem, novoProcesso.referenciaOrigemId]);

  const totalAssinantesNovaAssinatura = useMemo(() => {
    const locatarios = partesContrato.locatarios.length;
    const fiadores = partesContrato.fiadores.length;

    const conjugesLocatarios = partesContrato.locatarios.filter((pessoa) => pessoa.conjuge && Boolean(conjugesSelecionados[pessoa.fichaId])).length;

    const conjugesFiadores = partesContrato.fiadores.filter((pessoa) => pessoa.conjuge && Boolean(conjugesSelecionados[pessoa.fichaId])).length;

    const assinantesEmpresa = Array.isArray(alcadaAssinaturaContrato?.assinantes) ? alcadaAssinaturaContrato.assinantes.length : 0;

    return Math.max(1, locatarios + fiadores + conjugesLocatarios + conjugesFiadores + assinantesEmpresa);
  }, [partesContrato, conjugesSelecionados, alcadaAssinaturaContrato]);

  const limitarOrdemAssinatura = (valor) => {
    const numero = Number(valor) || 1;

    return Math.min(totalAssinantesNovaAssinatura, Math.max(1, numero));
  };

  const atualizarEmailParte = (grupo, fichaId, email) => {
    setPartesContrato((prev) => ({
      ...prev,
      [grupo]: (prev[grupo] ?? []).map((pessoa) =>
        pessoa.fichaId === fichaId
          ? {
              ...pessoa,
              email,
            }
          : pessoa,
      ),
    }));
  };
  const atualizarOrdemParte = (grupo, fichaId, ordemAssinatura) => {
    setPartesContrato((prev) => ({
      ...prev,
      [grupo]: (prev[grupo] ?? []).map((pessoa) =>
        pessoa.fichaId === fichaId
          ? {
              ...pessoa,
              ordemAssinatura,
            }
          : pessoa,
      ),
    }));
  };
  const atualizarSignatarioParte = (grupo, fichaId, signatario) => {
    setPartesContrato((prev) => ({
      ...prev,
      [grupo]: (prev[grupo] ?? []).map((pessoa) =>
        pessoa.fichaId === fichaId
          ? {
              ...pessoa,
              signatario,
            }
          : pessoa,
      ),
    }));
  };

  const atualizarCienciaParte = (grupo, fichaId, ciencia) => {
    setPartesContrato((prev) => ({
      ...prev,
      [grupo]: (prev[grupo] ?? []).map((pessoa) =>
        pessoa.fichaId === fichaId
          ? {
              ...pessoa,
              ciencia,
            }
          : pessoa,
      ),
    }));
  };

  const atualizarDescricaoCienciaParte = (grupo, fichaId, descricaoCiencia) => {
    setPartesContrato((prev) => ({
      ...prev,
      [grupo]: (prev[grupo] ?? []).map((pessoa) =>
        pessoa.fichaId === fichaId
          ? {
              ...pessoa,
              descricaoCiencia,
            }
          : pessoa,
      ),
    }));
  };

  const atualizarOrdemConjuge = (grupo, fichaId, ordemAssinatura) => {
    setPartesContrato((prev) => ({
      ...prev,
      [grupo]: (prev[grupo] ?? []).map((pessoa) =>
        pessoa.fichaId === fichaId && pessoa.conjuge
          ? {
              ...pessoa,
              conjuge: {
                ...pessoa.conjuge,
                ordemAssinatura,
              },
            }
          : pessoa,
      ),
    }));
  };
  const atualizarEmailConjuge = (grupo, fichaId, email) => {
    setPartesContrato((prev) => ({
      ...prev,
      [grupo]: (prev[grupo] ?? []).map((pessoa) =>
        pessoa.fichaId === fichaId && pessoa.conjuge
          ? {
              ...pessoa,
              conjuge: {
                ...pessoa.conjuge,
                email,
              },
            }
          : pessoa,
      ),
    }));
  };

  const atualizarSignatarioConjuge = (grupo, fichaId, signatario) => {
    setPartesContrato((prev) => ({
      ...prev,
      [grupo]: (prev[grupo] ?? []).map((pessoa) =>
        pessoa.fichaId === fichaId && pessoa.conjuge
          ? {
              ...pessoa,
              conjuge: {
                ...pessoa.conjuge,
                signatario,
              },
            }
          : pessoa,
      ),
    }));
  };

  const atualizarCienciaConjuge = (grupo, fichaId, ciencia) => {
    setPartesContrato((prev) => ({
      ...prev,
      [grupo]: (prev[grupo] ?? []).map((pessoa) =>
        pessoa.fichaId === fichaId && pessoa.conjuge
          ? {
              ...pessoa,
              conjuge: {
                ...pessoa.conjuge,
                ciencia,
                descricaoCiencia: ciencia === "SE APLICA" ? pessoa.conjuge.descricaoCiencia || "" : "",
              },
            }
          : pessoa,
      ),
    }));
  };

  const atualizarDescricaoCienciaConjuge = (grupo, fichaId, descricaoCiencia) => {
    setPartesContrato((prev) => ({
      ...prev,
      [grupo]: (prev[grupo] ?? []).map((pessoa) =>
        pessoa.fichaId === fichaId && pessoa.conjuge
          ? {
              ...pessoa,
              conjuge: {
                ...pessoa.conjuge,
                descricaoCiencia,
              },
            }
          : pessoa,
      ),
    }));
  };

  const atualizarEmailAssinanteEmpresa = (assinanteId, email) => {
    setAlcadaAssinaturaContrato((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        assinantes: (prev.assinantes ?? []).map((assinante) =>
          assinante.id === assinanteId
            ? {
                ...assinante,
                email,
              }
            : assinante,
        ),
      };
    });
  };
  const atualizarOrdemAssinanteEmpresa = (assinanteId, ordemAssinatura) => {
    setAlcadaAssinaturaContrato((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        assinantes: (prev.assinantes ?? []).map((assinante) =>
          assinante.id === assinanteId
            ? {
                ...assinante,
                ordemAssinatura,
              }
            : assinante,
        ),
      };
    });
  };
  const atualizarSignatarioAssinanteEmpresa = (assinanteId, signatario) => {
    setAlcadaAssinaturaContrato((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        assinantes: (prev.assinantes ?? []).map((assinante) =>
          assinante.id === assinanteId
            ? {
                ...assinante,
                signatario,
              }
            : assinante,
        ),
      };
    });
  };

  const atualizarCienciaAssinanteEmpresa = (assinanteId, ciencia) => {
    setAlcadaAssinaturaContrato((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        assinantes: (prev.assinantes ?? []).map((assinante) =>
          assinante.id === assinanteId
            ? {
                ...assinante,
                ciencia,
                descricaoCiencia: ciencia === "SE APLICA" ? assinante.descricaoCiencia || "" : "",
              }
            : assinante,
        ),
      };
    });
  };

  const atualizarDescricaoCienciaAssinanteEmpresa = (assinanteId, descricaoCiencia) => {
    setAlcadaAssinaturaContrato((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        assinantes: (prev.assinantes ?? []).map((assinante) =>
          assinante.id === assinanteId
            ? {
                ...assinante,
                descricaoCiencia,
              }
            : assinante,
        ),
      };
    });
  };
  const carregarDocumentosAprovados = useCallback(async (tipo) => {
    try {
      setCarregandoDocumentosAprovados(true);

      const response = await fetch(`/api/assinatura-final/documentos-aprovados?tipo=${tipo}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.erro || "Erro ao carregar documentos aprovados.");
      }

      setDocumentosAprovados(data.sucesso && Array.isArray(data.data) ? data.data : []);
    } catch (error) {
      console.error("Erro ao carregar documentos aprovados:", error);
      mostrarSnack("Erro ao carregar propostas/contratos aprovados.", "error");
      setDocumentosAprovados([]);
    } finally {
      setCarregandoDocumentosAprovados(false);
    }
  }, []);

  useEffect(() => {
    if (!formNovaAssinaturaAberto || !novoProcesso.tipoDocumentoOrigem) return;
    carregarDocumentosAprovados(novoProcesso.tipoDocumentoOrigem);
  }, [formNovaAssinaturaAberto, novoProcesso.tipoDocumentoOrigem, carregarDocumentosAprovados]);

  // Documento a ser enviado ao DocuSign: gerado automaticamente a partir do
  // registro selecionado em "Documento Aprovado". Os dois vêm prontos do backend
  // (docx montado no servidor + conversão por LibreOffice) — a Proposta usa o
  // mesmo endpoint do botão "Imprimir" da tela de Proposta Comercial, e o
  // Contrato o mesmo download já usado na tela de Contrato.
  const [gerandoDocumento, setGerandoDocumento] = useState(false);

  const gerarDocumentoDaProposta = useCallback(async (id) => {
    const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

    const resPdf = await fetch(`/api/propostas/${id}/pdf`, { headers });

    if (!resPdf.ok) {
      throw new Error("Erro ao gerar o PDF da proposta.");
    }

    const blob = await resPdf.blob();

    return new File([blob], nomeArquivoRespostaOuPadrao(resPdf, `proposta-${id}.pdf`), { type: "application/pdf" });
  }, []);

  const gerarDocumentoDoContrato = useCallback(async (id) => {
    const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

    const resVersoes = await fetch(`/api/contratos/${id}/versoes`, { headers });

    if (!resVersoes.ok) {
      throw new Error("Erro ao carregar as versões do contrato.");
    }

    const versoes = await resVersoes.json();
    const versaoAprovada = Array.isArray(versoes) ? versoes[0] : null;

    if (!versaoAprovada) {
      throw new Error("Contrato não possui versão aprovada.");
    }

    const resPdf = await fetch(`/api/contratos/${id}/versoes/${versaoAprovada.id}/download-pdf`, { headers });

    if (!resPdf.ok) {
      throw new Error("Erro ao gerar o PDF do contrato aprovado.");
    }

    const blob = await resPdf.blob();
    const nomeBase = versaoAprovada.nomeOriginal ? versaoAprovada.nomeOriginal.replace(/\.[^.]+$/, "") : `contrato-${id}`;

    return new File([blob], `${nomeBase}.pdf`, { type: "application/pdf" });
  }, []);

  useEffect(() => {
    if (
      !formNovaAssinaturaAberto ||
      !novoProcesso.referenciaOrigemId ||
      !novoProcesso.tipoDocumentoOrigem ||
      novoProcesso.modalidadeAssinatura !== "DOCUSIGN"
    ) {
      return;
    }

    let cancelado = false;

    (async () => {
      try {
        setGerandoDocumento(true);
        setArquivoDocusignTeste(null);

        const arquivo =
          novoProcesso.tipoDocumentoOrigem === "PROPOSTA"
            ? await gerarDocumentoDaProposta(novoProcesso.referenciaOrigemId)
            : await gerarDocumentoDoContrato(novoProcesso.referenciaOrigemId);

        if (!cancelado) setArquivoDocusignTeste(arquivo);
      } catch (error) {
        console.error("Erro ao gerar documento para assinatura:", error);
        if (!cancelado) mostrarSnack(error.message || "Erro ao gerar o documento para assinatura.", "error");
      } finally {
        if (!cancelado) setGerandoDocumento(false);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [
    formNovaAssinaturaAberto,
    novoProcesso.referenciaOrigemId,
    novoProcesso.tipoDocumentoOrigem,
    novoProcesso.modalidadeAssinatura,
    gerarDocumentoDaProposta,
    gerarDocumentoDoContrato,
  ]);

  const mostrarSnack = (msg, severity = "success") => {
    setSnack({
      aberto: true,
      msg,
      severity,
    });
  };

  const carregarEnvelopes = useCallback(async (silencioso = false) => {
    try {
      if (!silencioso) {
        setCarregando(true);
      }

      const response = await fetch("/api/assinatura-final", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.erro || "Erro ao carregar envelopes.");
      }

      if (data.sucesso) {
        setEnvelopes(Array.isArray(data.data) ? data.data : []);
      } else {
        setEnvelopes([]);
      }
    } catch (error) {
      console.error("Erro ao carregar envelopes:", error);

      if (!silencioso) {
        mostrarSnack("Erro ao carregar envios para assinatura.", "error");
      }
    } finally {
      if (!silencioso) {
        setCarregando(false);
      }
    }
  }, []);

  const handleNovaAssinatura = () => {
    setNovoProcesso({
      tipoDocumentoOrigem: "PROPOSTA",
      referenciaOrigemId: "",
      tituloContrato: "",
      modalidadeAssinatura: "DOCUSIGN",
      observacoes: "",
    });

    setAlcadaAssinaturaContrato(null);

    setPartesContrato({
      locatarios: [],
      fiadores: [],
    });

    setConjugesSelecionados({});
    setCarregandoPartesContrato(false);

    setArquivoDocusignTeste(null);

    setModalSelecionarDocumentoAprovado(false);
    setBuscaDocumentoAprovado("");
    setPaginaDocumentoAprovado(0);
    setPorPaginaDocumentoAprovado(10);

    setFormNovaAssinaturaAberto(true);
  };

  const handleAbrirSelecionarDocumentoAprovado = () => {
    setBuscaDocumentoAprovado("");
    setPaginaDocumentoAprovado(0);
    setModalSelecionarDocumentoAprovado(true);
  };

  const handleFecharSelecionarDocumentoAprovado = () => {
    setModalSelecionarDocumentoAprovado(false);
  };

  const handleSelecionarDocumentoAprovado = (item) => {
    setNovoProcesso((prev) => ({
      ...prev,
      referenciaOrigemId: String(item.id),
      tituloContrato: item.titulo || "",
    }));

    setModalSelecionarDocumentoAprovado(false);
    setBuscaDocumentoAprovado("");
    setPaginaDocumentoAprovado(0);
  };

  const handleSalvarNovaAssinatura = async () => {
    try {
      if (!novoProcesso.referenciaOrigemId) {
        mostrarSnack("Selecione uma proposta ou contrato.", "warning");
        return;
      }

      if (gerandoDocumento) {
        mostrarSnack("Aguarde a geração do documento antes de salvar.", "warning");
        return;
      }

      if (novoProcesso.modalidadeAssinatura === "DOCUSIGN" && !arquivoDocusignTeste) {
        mostrarSnack("Não foi possível gerar o documento para assinatura. Selecione o documento novamente.", "error");
        return;
      }

      const assinantes = [];

      const emailObrigatorio = novoProcesso.modalidadeAssinatura === "DOCUSIGN";

      const tipoEhContrato = novoProcesso.tipoDocumentoOrigem === "CONTRATO";

      const nomeOrigem = tipoEhContrato ? "contrato" : "proposta comercial";

      /*
       * ============================================================
       * PARTES DO DOCUMENTO
       * ============================================================
       */

      if (carregandoPartesContrato) {
        mostrarSnack(`Aguarde o carregamento das partes do ${nomeOrigem}.`, "warning");
        return;
      }

      if (partesContrato.locatarios.length === 0) {
        mostrarSnack(
          tipoEhContrato ? "Não foi encontrado locatário vinculado ao contrato." : "Não foi encontrado locatário vinculado à proposta comercial.",
          "warning",
        );
        return;
      }

      const partesDocumento = [
        ...partesContrato.locatarios.map((pessoa) => ({
          ...pessoa,
          papel: "LOCATARIO",
        })),

        ...partesContrato.fiadores.map((pessoa) => ({
          ...pessoa,
          papel: "FIADOR",
        })),
      ];

      for (const pessoa of partesDocumento) {
        if (!pessoa.nome?.trim()) {
          mostrarSnack(`${pessoa.papel === "FIADOR" ? "Fiador" : "Locatário"} sem nome cadastrado.`, "error");
          return;
        }

        if (emailObrigatorio && !pessoa.email?.trim()) {
          mostrarSnack(`${pessoa.nome} não possui e-mail para assinatura.`, "warning");
          return;
        }

        assinantes.push({
          nome: pessoa.nome,
          email: pessoa.email?.trim() || null,
          tipo: pessoa.papel,
          ordem: Number(pessoa.ordemAssinatura || 1),
          signatario: pessoa.signatario || "",
          ciencia: pessoa.ciencia || "NÃO SE APLICA",
          descricaoCiencia: pessoa.ciencia === "SE APLICA" ? pessoa.descricaoCiencia?.trim() || "" : "",
        });

        const incluirConjuge = Boolean(pessoa.conjuge && conjugesSelecionados[pessoa.fichaId]);

        if (incluirConjuge) {
          if (!pessoa.conjuge.nome?.trim()) {
            mostrarSnack(`O cônjuge de ${pessoa.nome} está sem nome cadastrado.`, "warning");
            return;
          }

          if (emailObrigatorio && !pessoa.conjuge.email?.trim()) {
            mostrarSnack(`O cônjuge de ${pessoa.nome} está sem e-mail para assinatura.`, "warning");
            return;
          }

          assinantes.push({
            nome: pessoa.conjuge.nome,
            email: pessoa.conjuge.email?.trim() || null,
            tipo: pessoa.papel === "FIADOR" ? "CONJUGE_FIADOR" : "CONJUGE_LOCATARIO",
            ordem: Number(pessoa.conjuge.ordemAssinatura || 1),
            signatario: pessoa.conjuge.signatario || "",
            ciencia: pessoa.conjuge.ciencia || "NÃO SE APLICA",
            descricaoCiencia: pessoa.conjuge.ciencia === "SE APLICA" ? pessoa.conjuge.descricaoCiencia?.trim() || "" : "",
          });
        }
      }

      /*
       * ============================================================
       * ASSINANTES DA EMPRESA - ALÇADA DE ASSINATURAS
       * ============================================================
       */

      if (carregandoAlcadaAssinatura) {
        mostrarSnack("Aguarde o carregamento dos assinantes da empresa.", "warning");
        return;
      }

      const assinantesEmpresa = Array.isArray(alcadaAssinaturaContrato?.assinantes)
        ? [...alcadaAssinaturaContrato.assinantes].sort((a, b) => Number(a.ordem) - Number(b.ordem))
        : [];

      const processoAlcada = tipoEhContrato ? "CONTRATO" : "PROPOSTA COMERCIAL";

      if (tipoEhContrato && assinantesEmpresa.length === 0) {
        mostrarSnack("Não existe Alçada de Assinaturas ativa configurada para CONTRATO.", "warning");
        return;
      }

      const assinanteSemNome = assinantesEmpresa.some((assinante) => !assinante.usuario?.nome?.trim());

      if (assinanteSemNome) {
        mostrarSnack("Existe assinante da empresa sem usuário/nome válido na Alçada de Assinaturas.", "error");
        return;
      }

      if (emailObrigatorio && assinantesEmpresa.some((assinante) => !assinante.email?.trim())) {
        mostrarSnack("Existe assinante da empresa sem e-mail para assinatura.", "warning");
        return;
      }

      assinantesEmpresa.forEach((assinante) => {
        assinantes.push({
          nome: assinante.usuario.nome,
          email: assinante.email?.trim() || null,
          tipo: assinante.identificacao || "ASSINANTE_EMPRESA",
          ordem: Number(assinante.ordemAssinatura || 1),
          signatario: assinante.signatario || assinante.identificacao || "REPRESENTANTE LEGAL",
          ciencia: assinante.ciencia || "NÃO SE APLICA",
          descricaoCiencia: assinante.ciencia === "SE APLICA" ? assinante.descricaoCiencia?.trim() || "" : "",
        });
      });

      const assinanteCienciaSemDescricao = assinantes.find((assinante) => assinante.ciencia === "SE APLICA" && !assinante.descricaoCiencia?.trim());

      if (assinanteCienciaSemDescricao) {
        mostrarSnack(`Informe a Ciência de ${assinanteCienciaSemDescricao.nome}.`, "warning");
        return;
      }

      const formData = new FormData();

      formData.append("tituloContrato", novoProcesso.tituloContrato);

      formData.append("origemModulo", novoProcesso.tipoDocumentoOrigem);

      formData.append("referenciaOrigemId", novoProcesso.referenciaOrigemId);

      formData.append("modalidadeAssinatura", novoProcesso.modalidadeAssinatura);

      formData.append("observacoes", novoProcesso.observacoes || "");

      formData.append("assinantes", JSON.stringify(assinantes));

      if (arquivoDocusignTeste) {
        formData.append("arquivo", arquivoDocusignTeste);
      }

      const response = await fetch("/api/assinatura-final", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.erro || "Erro ao criar assinatura.");
      }

      setFormNovaAssinaturaAberto(false);

      mostrarSnack("Assinatura criada com sucesso.", "success");

      carregarEnvelopes();
    } catch (error) {
      console.error(error);

      mostrarSnack(error.message || "Erro ao criar assinatura.", "error");
    }
  };

  const handleVisualizar = async (envelope) => {
    try {
      /*
       * Fecha imediatamente qualquer processo que esteja
       * sendo exibido antes de carregar o novo selecionado.
       */
      setModalDetalhes(false);
      setAssinaturaSelecionada(null);

      setDocumentosAssinatura([]);
      setEventosAssinatura([]);
      setParticipantesAssinatura([]);

      setArquivoFisico(null);

      setAbaDetalhes(0);

      editandoAssinantesRef.current = false;
      setEditandoAssinantes(false);

      setObservacoesDetalhes("");
      setEditandoObservacoes(false);

      const response = await fetch(`/api/assinatura-final/${envelope.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.erro || "Erro ao carregar detalhes.");
      }

      setAssinaturaSelecionada(data.data.processo || envelope);

      setDocumentosAssinatura(data.data.documentos || []);

      setEventosAssinatura(data.data.eventos || []);

      setParticipantesAssinatura(data.data.participantes || []);

      setStatusFisico(data.data.processo?.statusFluxo || "RASCUNHO");

      setObservacoesDetalhes(data.data.processo?.observacoes || "");

      /*
       * Somente depois que o novo processo estiver carregado,
       * a área de detalhes volta a ser exibida.
       */
      setModalDetalhes(true);
    } catch (error) {
      console.error(error);

      mostrarSnack(error.message || "Erro ao carregar detalhes.", "error");
    }
  };
  const handleEnviar = async (envelope) => {
    if (enviandoId === envelope.id) {
      return;
    }

    try {
      setEnviandoId(envelope.id);

      const response = await fetch(`/api/assinatura-final/${envelope.id}/enviar`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.erro || "Erro ao enviar assinatura externa.");
      }

      mostrarSnack(data.mensagem || "Envelope DocuSign criado e enviado com sucesso.", "success");

      carregarEnvelopes();
    } catch (error) {
      console.error(error);

      mostrarSnack(error.message || "Erro ao enviar assinatura externa.", "error");
    } finally {
      setEnviandoId(null);
    }
  };

  const sincronizarStatusAutomaticamente = useCallback(async () => {
    if (sincronizandoAutomaticamenteRef.current) {
      return;
    }

    const token = localStorage.getItem("token");

    if (!token) {
      return;
    }

    try {
      sincronizandoAutomaticamenteRef.current = true;

      const response = await fetch("/api/assinatura-final", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.erro || "Erro ao carregar assinaturas para sincronização.");
      }

      const lista = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];

      const processosParaSincronizar = lista.filter((processo) => {
        const modalidade = String(processo.modalidadeAssinatura || processo.modalidade || "").toUpperCase();

        const status = String(processo.statusFluxo || processo.status || "").toUpperCase();

        return modalidade === "DOCUSIGN" && Boolean(processo.docusignEnvelopeId) && !["ASSINADO", "ARQUIVADO", "CANCELADO"].includes(status);
      });

      await Promise.allSettled(
        processosParaSincronizar.map(async (processo) => {
          const statusResponse = await fetch(`/api/assinatura-final/${processo.id}/status-docusign?automatico=1`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!statusResponse.ok) {
            const erro = await statusResponse.json().catch(() => ({}));

            throw new Error(erro.erro || `Erro ao sincronizar assinatura ${processo.id}.`);
          }
        }),
      );

      await carregarEnvelopes(true);

      if (modalDetalhes && assinaturaSelecionada?.id) {
        const detalhesResponse = await fetch(`/api/assinatura-final/${assinaturaSelecionada.id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const detalhes = await detalhesResponse.json();

        if (detalhesResponse.ok && detalhes?.data) {
          setAssinaturaSelecionada((prev) => ({
            ...(prev || {}),
            ...(detalhes.data.processo || {}),
          }));

          /*
           * Enquanto estiver em edição, NUNCA sobrescrever
           * os dados do formulário de assinantes.
           *
           * O ref é usado aqui para impedir que uma requisição
           * automática iniciada antes do clique em "Editar Assinantes"
           * apague o que o usuário está digitando.
           */
          if (!editandoAssinantesRef.current) {
            setParticipantesAssinatura(detalhes.data.participantes || []);
          }

          /*
           * Estes dados continuam sendo atualizados automaticamente
           * mesmo durante a edição dos assinantes.
           */
          setDocumentosAssinatura(detalhes.data.documentos || []);

          setEventosAssinatura(detalhes.data.eventos || []);

          setStatusFisico(detalhes.data.processo?.statusFluxo || "RASCUNHO");
        }
      }
    } catch (error) {
      console.error("Erro na sincronização automática DocuSign:", error);
    } finally {
      sincronizandoAutomaticamenteRef.current = false;
    }
  }, [carregarEnvelopes, modalDetalhes, assinaturaSelecionada?.id]);

  useEffect(() => {
    carregarEnvelopes();

    const intervaloAtualizacao = setInterval(() => {
      sincronizarStatusAutomaticamente();
    }, 5000);

    return () => {
      clearInterval(intervaloAtualizacao);
    };
  }, [carregarEnvelopes, sincronizarStatusAutomaticamente]);

  const handleArquivar = async (envelope) => {
    try {
      const modalidade = String(modalidadeProcesso(envelope) || "").toUpperCase();

      /*
       * Arquivamento manual disponível somente
       * para assinatura física.
       */
      if (!["FISICA", "FISICO"].includes(modalidade)) {
        mostrarSnack("O arquivamento manual está disponível somente para assinaturas físicas.", "warning");
        return;
      }

      const confirmar = window.confirm("Confirma o arquivamento desta assinatura física?");

      if (!confirmar) {
        return;
      }

      const response = await fetch(`/api/assinatura-final/${envelope.id}/arquivar`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.erro || "Erro ao arquivar assinatura.");
      }

      mostrarSnack(data.mensagem || "Assinatura física arquivada com sucesso.", "success");

      /*
       * Se o processo arquivado estiver aberto,
       * fecha os detalhes.
       */
      if (String(assinaturaSelecionada?.id) === String(envelope.id)) {
        setModalDetalhes(false);
        setAssinaturaSelecionada(null);

        setDocumentosAssinatura([]);
        setEventosAssinatura([]);
        setParticipantesAssinatura([]);
      }

      await carregarEnvelopes();
    } catch (error) {
      console.error(error);

      mostrarSnack(error.message || "Erro ao arquivar assinatura.", "error");
    }
  };

  const handleSalvarStatusFisico = async () => {
    try {
      const response = await fetch(`/api/assinatura-final/${assinaturaSelecionada.id}/status-fisico`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          statusFluxo: statusFisico,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.erro || "Erro ao atualizar status.");
      }

      mostrarSnack("Status físico atualizado com sucesso.", "success");

      carregarEnvelopes();
    } catch (error) {
      console.error(error);
      mostrarSnack(error.message, "error");
    }
  };

  const obterUrlDocumento = (doc) => {
    if (!doc) return "";

    if (doc.nomeSalvo) {
      return `/api/assinatura-final/documentos/${doc.id}/arquivo`;
    }

    return "";
  };

  const abrirDocumentoGerado = () => {
    if (!arquivoDocusignTeste) return;

    const blobUrl = window.URL.createObjectURL(arquivoDocusignTeste);
    window.open(blobUrl, "_blank", "noopener,noreferrer");
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
  };

  const baixarDocumentoGerado = () => {
    if (!arquivoDocusignTeste) return;

    const blobUrl = window.URL.createObjectURL(arquivoDocusignTeste);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = arquivoDocusignTeste.name || "documento.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  const excluirDocumentoGerado = () => {
    setArquivoDocusignTeste(null);
  };

  const abrirDocumentoAutenticado = async (doc, baixar = false) => {
    try {
      const url = obterUrlDocumento(doc);

      if (!url) {
        mostrarSnack("Arquivo não encontrado.", "warning");
        return;
      }

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error("Erro ao acessar documento.");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      if (baixar) {
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = doc.nomeOriginal || "documento.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        return;
      }

      window.open(blobUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error(error);
      mostrarSnack(error.message || "Erro ao acessar documento.", "error");
    }
  };

  const handleVisualizarDocumento = (doc) => {
    abrirDocumentoAutenticado(doc, false);
  };

  const handleBaixarDocumento = (doc) => {
    abrirDocumentoAutenticado(doc, true);
  };

  const handleSalvarAssinantes = async () => {
    try {
      if (!assinaturaSelecionada?.id) {
        mostrarSnack("Nenhuma assinatura selecionada.", "warning");
        return;
      }

      const assinanteCienciaSemDescricao = participantesAssinatura.find(
        (participante) => String(participante.ciencia || "").toUpperCase() === "SE APLICA" && !String(participante.descricaoCiencia || "").trim(),
      );

      if (assinanteCienciaSemDescricao) {
        mostrarSnack(`Informe a Ciência de ${assinanteCienciaSemDescricao.nome}.`, "warning");
        return;
      }

      const confirmar = window.confirm("Confirma a alteração dos dados dos assinantes?");

      if (!confirmar) {
        return;
      }

      const response = await fetch(`/api/assinatura-final/${assinaturaSelecionada.id}/participantes`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          participantes: participantesAssinatura.map((participante) => ({
            id: participante.id,
            nome: participante.nome,
            email: participante.email,
            signatario: participante.signatario || "",
            ciencia: participante.ciencia || "NÃO SE APLICA",
            descricaoCiencia: participante.ciencia === "SE APLICA" ? participante.descricaoCiencia || "" : "",
          })),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.erro || "Erro ao salvar os dados dos assinantes.");
      }

      mostrarSnack("Dados dos assinantes atualizados com sucesso.", "success");

      editandoAssinantesRef.current = false;
      setEditandoAssinantes(false);

      const detalhesResponse = await fetch(`/api/assinatura-final/${assinaturaSelecionada.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const detalhes = await detalhesResponse.json();

      if (detalhesResponse.ok && detalhes?.data) {
        setAssinaturaSelecionada((prev) => ({
          ...(prev || {}),
          ...(detalhes.data.processo || {}),
        }));

        setParticipantesAssinatura(detalhes.data.participantes || []);

        setDocumentosAssinatura(detalhes.data.documentos || []);

        setEventosAssinatura(detalhes.data.eventos || []);
      }

      carregarEnvelopes();
    } catch (error) {
      console.error(error);

      mostrarSnack(error.message, "error");
    }
  };
  const handleSalvarObservacoes = async () => {
    try {
      if (!assinaturaSelecionada?.id) {
        mostrarSnack("Nenhuma assinatura selecionada.", "warning");
        return;
      }

      const confirmar = window.confirm("Confirma a alteração da observação deste processo?");

      if (!confirmar) {
        return;
      }

      const response = await fetch(`/api/assinatura-final/${assinaturaSelecionada.id}/observacoes`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          observacoes: observacoesDetalhes,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.erro || "Erro ao salvar observação.");
      }

      mostrarSnack("Observação atualizada com sucesso.", "success");

      setEditandoObservacoes(false);

      await handleVisualizar({
        id: assinaturaSelecionada.id,
      });

      carregarEnvelopes();
    } catch (error) {
      console.error(error);
      mostrarSnack(error.message, "error");
    }
  };

  const handleUploadFisico = async () => {
    try {
      if (!arquivoFisico) {
        mostrarSnack("Selecione um PDF para enviar.", "warning");
        return;
      }

      const formData = new FormData();

      formData.append("arquivo", arquivoFisico);

      const response = await fetch(`/api/assinatura-final/${assinaturaSelecionada.id}/upload-documento-fisico`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.erro || "Erro ao enviar documento.");
      }

      mostrarSnack("Documento enviado com sucesso.", "success");

      carregarEnvelopes();
    } catch (error) {
      console.error(error);
      mostrarSnack(error.message, "error");
    }
  };

  const tipoEnvelopeLabel = (envelope) => {
    if (envelope?.origemModulo === "PROPOSTA") {
      return "Proposta Comercial";
    }

    if (envelope?.origemModulo === "CONTRATO") {
      return "Contrato";
    }

    return tipoDocumentoLabel(envelope?.origemModulo);
  };

  const envelopesFiltrados = useMemo(() => {
    return envelopes.filter((item) => {
      const textoBusca = filtroTexto.trim().toUpperCase();

      const documento = String(item.tituloContrato || "").toUpperCase();

      const tipo = String(tipoEnvelopeLabel(item) || "").toUpperCase();

      const modalidade = String(modalidadeLabel(item.modalidadeAssinatura) || "").toUpperCase();

      const status = String(statusLabel(item.statusFluxo || item.status) || "").toUpperCase();

      const assinantes = String(item.totalAssinantes ?? 0);

      const documentos = String(item.totalDocumentos ?? 0);

      const criadoEm = item.criadoEm ? new Date(item.criadoEm).toLocaleString("pt-BR") : "";

      const correspondeTexto =
        !textoBusca || documento.includes(textoBusca) || tipo.includes(textoBusca) || modalidade.includes(textoBusca) || status.includes(textoBusca);

      const correspondeStatus = filtroStatus === "TODOS" || item.statusFluxo === filtroStatus;

      const correspondeTipo = filtroTipo === "TODOS" || item.origemModulo === filtroTipo;

      const correspondeDocumento = !filtrosColuna.DOCUMENTO || documento.includes(filtrosColuna.DOCUMENTO.trim().toUpperCase());

      const correspondeTipoColuna = filtrosColuna.TIPO.length === 0 || filtrosColuna.TIPO.includes(tipo);

      const correspondeModalidade = filtrosColuna.MODALIDADE.length === 0 || filtrosColuna.MODALIDADE.includes(modalidade);

      const correspondeStatusColuna = filtrosColuna.STATUS.length === 0 || filtrosColuna.STATUS.includes(status);

      const correspondeAssinantes = filtrosColuna.ASSINANTES === "" || Number(assinantes) === Number(filtrosColuna.ASSINANTES);

      const correspondeDocumentos = filtrosColuna.DOCUMENTOS === "" || Number(documentos) === Number(filtrosColuna.DOCUMENTOS);

      const dataCriacao = item.criadoEm ? new Date(item.criadoEm) : null;

      const dataInicial = filtrosColuna.CRIADO_EM_DE ? new Date(`${filtrosColuna.CRIADO_EM_DE}T00:00:00`) : null;

      const dataFinal = filtrosColuna.CRIADO_EM_ATE ? new Date(`${filtrosColuna.CRIADO_EM_ATE}T23:59:59`) : null;

      const correspondeCriadoEm = (!dataInicial || (dataCriacao && dataCriacao >= dataInicial)) && (!dataFinal || (dataCriacao && dataCriacao <= dataFinal));

      return (
        correspondeTexto &&
        correspondeStatus &&
        correspondeTipo &&
        correspondeDocumento &&
        correspondeTipoColuna &&
        correspondeModalidade &&
        correspondeStatusColuna &&
        correspondeAssinantes &&
        correspondeDocumentos &&
        correspondeCriadoEm
      );
    });
  }, [envelopes, filtroTexto, filtroStatus, filtroTipo, filtrosColuna]);

  const alterarOrdenacao = (coluna) => {
    setPagina(0);

    if (colunaOrdenacao === coluna) {
      setDirecaoOrdenacao((direcaoAtual) => (direcaoAtual === "ASC" ? "DESC" : "ASC"));

      return;
    }

    setColunaOrdenacao(coluna);
    setDirecaoOrdenacao("ASC");
  };

  const envelopesOrdenados = useMemo(() => {
    const lista = [...envelopesFiltrados];

    lista.sort((a, b) => {
      let valorA = "";
      let valorB = "";

      switch (colunaOrdenacao) {
        case "DOCUMENTO":
          valorA = a.tituloContrato || "";
          valorB = b.tituloContrato || "";
          break;

        case "TIPO":
          valorA = tipoEnvelopeLabel(a);
          valorB = tipoEnvelopeLabel(b);
          break;

        case "MODALIDADE":
          valorA = modalidadeLabel(a.modalidadeAssinatura);
          valorB = modalidadeLabel(b.modalidadeAssinatura);
          break;

        case "STATUS":
          valorA = statusLabel(a.statusFluxo || a.status);
          valorB = statusLabel(b.statusFluxo || b.status);
          break;

        case "ASSINANTES":
          valorA = Number(a.totalAssinantes || 0);
          valorB = Number(b.totalAssinantes || 0);
          break;

        case "DOCUMENTOS":
          valorA = Number(a.totalDocumentos || 0);
          valorB = Number(b.totalDocumentos || 0);
          break;

        case "CRIADO_EM":
          valorA = a.criadoEm ? new Date(a.criadoEm).getTime() : 0;

          valorB = b.criadoEm ? new Date(b.criadoEm).getTime() : 0;
          break;

        default:
          return 0;
      }

      let resultado = 0;

      if (["ASSINANTES", "DOCUMENTOS", "CRIADO_EM"].includes(colunaOrdenacao)) {
        resultado = valorA - valorB;
      } else {
        resultado = String(valorA).localeCompare(String(valorB), "pt-BR", {
          sensitivity: "base",
          numeric: true,
        });
      }

      return direcaoOrdenacao === "ASC" ? resultado : -resultado;
    });

    return lista;
  }, [envelopesFiltrados, colunaOrdenacao, direcaoOrdenacao]);

  const handleCopiar = async () => {
    const texto = envelopesFiltrados
      .map((envelope) => {
        const documento = envelope.tituloContrato || "—";

        const tipo = tipoEnvelopeLabel(envelope);

        const modalidade = modalidadeLabel(envelope.modalidadeAssinatura);

        const status = statusLabel(envelope.statusFluxo || envelope.status);

        const assinantes = envelope.totalAssinantes ?? 0;

        const documentos = envelope.totalDocumentos ?? 0;

        const criadoEm = envelope.criadoEm ? new Date(envelope.criadoEm).toLocaleString("pt-BR") : "—";

        return [documento, tipo, modalidade, status, `${assinantes} assinante(s)`, `${documentos} documento(s)`, criadoEm].join(" - ");
      })
      .join("\n");

    if (!texto.trim()) {
      alert("Nada para copiar (lista vazia).");
      return;
    }

    const ok = await copiarTexto(texto);

    alert(ok ? "Copiado!" : "Não foi possível copiar.");
  };

  // =======================
  // CSV
  // =======================
  const handleCSV = () => {
    if (envelopesFiltrados.length === 0) {
      alert("Nada para exportar (lista vazia).");
      return;
    }

    const BOM = "\uFEFF";

    const header = ["Documento", "Tipo", "Modalidade", "Status da Assinatura", "Assinantes", "Documentos", "Criado em"];

    const csvEscape = (value) => {
      const texto = String(value ?? "");

      if (/[;"\n\r]/.test(texto)) {
        return `"${texto.replace(/"/g, '""')}"`;
      }

      return texto;
    };

    const linhas = envelopesFiltrados.map((envelope) =>
      [
        envelope.tituloContrato || "—",

        envelope.origemModulo === "PROPOSTA"
          ? "Proposta Comercial"
          : envelope.origemModulo === "CONTRATO"
            ? "Contrato"
            : tipoDocumentoLabel(envelope.origemModulo),

        modalidadeLabel(envelope.modalidadeAssinatura),

        statusLabel(envelope.statusFluxo || envelope.status),

        envelope.totalAssinantes ?? 0,

        envelope.totalDocumentos ?? 0,

        envelope.criadoEm ? new Date(envelope.criadoEm).toLocaleString("pt-BR") : "—",
      ].map(csvEscape),
    );

    const csv = [header.map(csvEscape), ...linhas].map((linha) => linha.join(";")).join("\n");

    const blob = new Blob([BOM + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;

    link.setAttribute("download", "assinaturas-final.csv");

    document.body.appendChild(link);

    link.click();

    link.remove();

    URL.revokeObjectURL(url);
  };

  const envelopesPaginados = envelopesOrdenados.slice(pagina * porPagina, pagina * porPagina + porPagina);

  const documentosDisponiveis = documentosAprovados.filter((documento) => {
    return !envelopes.some((envelope) => {
      return String(envelope.referenciaOrigemId) === String(documento.id) && envelope.origemModulo === novoProcesso.tipoDocumentoOrigem;
    });
  });

  const documentoAprovadoSelecionado = documentosAprovados.find((item) => String(item.id) === String(novoProcesso.referenciaOrigemId));

  const documentosDisponiveisFiltrados = documentosDisponiveis.filter((item) => {
    const textoBusca = buscaDocumentoAprovado.trim().toUpperCase();

    if (!textoBusca) {
      return true;
    }

    return String(item.titulo || "")
      .toUpperCase()
      .includes(textoBusca);
  });

  const documentosDisponiveisPaginados = documentosDisponiveisFiltrados.slice(
    paginaDocumentoAprovado * porPaginaDocumentoAprovado,
    paginaDocumentoAprovado * porPaginaDocumentoAprovado + porPaginaDocumentoAprovado,
  );

  const processoBloqueado =
    assinaturaSelecionada &&
    ["ENVIADO", "AGUARDANDO_ASSINATURAS", "PARCIALMENTE_ASSINADO", "ASSINADO", "ARQUIVADO", "CANCELADO"].includes(assinaturaSelecionada.statusFluxo);

  const abrirFiltroColuna = (event, coluna) => {
    event.stopPropagation();

    setFiltroColunaAberto(coluna);
    setAncoraFiltroColuna(event.currentTarget);
  };

  const fecharFiltroColuna = () => {
    setFiltroColunaAberto(null);
    setAncoraFiltroColuna(null);
  };

  const alterarFiltroColuna = (coluna, valor) => {
    setFiltrosColuna((prev) => ({
      ...prev,
      [coluna]: valor,
    }));

    setPagina(0);
  };

  const alternarFiltroMultiplo = (coluna, valor) => {
    setFiltrosColuna((prev) => {
      const valoresAtuais = Array.isArray(prev[coluna]) ? prev[coluna] : [];

      const jaSelecionado = valoresAtuais.includes(valor);

      return {
        ...prev,
        [coluna]: jaSelecionado ? valoresAtuais.filter((item) => item !== valor) : [...valoresAtuais, valor],
      };
    });

    setPagina(0);
  };

  const indicadorOrdenacao = (coluna) => {
    if (colunaOrdenacao !== coluna) {
      return "↕";
    }

    return direcaoOrdenacao === "ASC" ? "↑" : "↓";
  };

  const cabecalhoFiltravel = (titulo, coluna) => {
    const filtroAtivo =
      coluna === "CRIADO_EM"
        ? Boolean(filtrosColuna.CRIADO_EM_DE || filtrosColuna.CRIADO_EM_ATE)
        : ["TIPO", "MODALIDADE", "STATUS"].includes(coluna)
          ? Array.isArray(filtrosColuna[coluna]) && filtrosColuna[coluna].length > 0
          : Boolean(String(filtrosColuna[coluna] || "").trim());

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          whiteSpace: "nowrap",
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => alterarOrdenacao(coluna)}
          sx={{
            border: 0,
            p: 0,
            m: 0,
            background: "transparent",
            color: "inherit",
            font: "inherit",
            fontWeight: "inherit",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {titulo}

          <Typography
            component="span"
            sx={{
              fontSize: "0.72rem",
              color: colunaOrdenacao === coluna ? "#2196f3" : "var(--ig-t-777777)",
            }}
          >
            {indicadorOrdenacao(coluna)}
          </Typography>
        </Box>

        <IconButton
          size="small"
          onClick={(event) => abrirFiltroColuna(event, coluna)}
          sx={{
            p: 0.4,
            color: filtroAtivo ? "#2196f3" : "var(--ig-t-777777)",

            backgroundColor: filtroAtivo ? "rgba(33, 150, 243, 0.10)" : "transparent",

            "&:hover": {
              color: "var(--ig-t-dddddd)",
              backgroundColor: "var(--ig-f-2a2a2a)",
            },
          }}
        >
          <FilterAltIcon
            sx={{
              fontSize: 15,
            }}
          />
        </IconButton>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ color: "var(--ig-t-ffffff)" }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
          Envio para Assinatura Final
        </Typography>

        <Typography variant="body2" sx={{ mb: 2, color: "var(--ig-t-aaaaaa)" }}>
          A assinatura do documento ocorre fora do ImobGest. O sistema apenas envia, acompanha o status externo e arquiva o contrato final assinado.
        </Typography>

        <Box sx={{ mb: 2 }}>
          <button
            type="button"
            onClick={handleNovaAssinatura}
            style={{
              border: "1px solid #2e7d32",
              color: "#fff",
              background: "#2e7d32",
              padding: "6px 14px",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            + Nova Assinatura
          </button>
        </Box>

        <Box
          sx={{
            mb: 2,
            p: 2,
            border: "1px solid var(--ig-b-2b2b2b)",
            borderRadius: 1,
            backgroundColor: "transparent",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 1.5,
              fontWeight: 600,
            }}
          >
            Assinaturas
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              width: "100%",
            }}
          >
            {/* BUSCAR */}
            <TextField
              size="small"
              placeholder="Buscar"
              value={filtroTexto}
              onChange={(e) => {
                setFiltroTexto(e.target.value);
                setPagina(0);
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon
                        sx={{
                          color: "var(--ig-t-9ca3af)",
                          fontSize: 20,
                        }}
                      />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                flex: 1,
                minWidth: 260,

                "& .MuiOutlinedInput-root": {
                  height: 40,
                  backgroundColor: "transparent",
                  color: "var(--ig-t-ffffff)",
                },

                "& input::placeholder": {
                  color: "var(--ig-t-ffffff)",
                  opacity: 1,
                  fontWeight: 500,
                  fontSize: "1rem",
                },
              }}
            />

            {/* TIPO */}
            <TextField
              select
              size="small"
              label="Tipo"
              value={filtroTipo}
              onChange={(e) => {
                setFiltroTipo(e.target.value);
                setPagina(0);
              }}
              sx={{
                width: 150,

                "& .MuiOutlinedInput-root": {
                  height: 40,
                },
              }}
            >
              <MenuItem value="TODOS">Todos</MenuItem>

              <MenuItem value="PROPOSTA">Propostas</MenuItem>

              <MenuItem value="CONTRATO">Contratos</MenuItem>
            </TextField>

            {/* STATUS */}
            <TextField
              select
              size="small"
              label="Status"
              value={filtroStatus}
              onChange={(e) => {
                setFiltroStatus(e.target.value);
                setPagina(0);
              }}
              sx={{
                width: 230,

                "& .MuiOutlinedInput-root": {
                  height: 40,
                },
              }}
            >
              <MenuItem value="TODOS">Todos</MenuItem>

              <MenuItem value="RASCUNHO">Rascunho</MenuItem>

              <MenuItem value="PRONTO_PARA_ENVIO">Pronto para Envio</MenuItem>

              <MenuItem value="ENVIADO">Enviado</MenuItem>

              <MenuItem value="AGUARDANDO_ASSINATURAS">Aguardando Assinaturas</MenuItem>

              <MenuItem value="ASSINADO">Assinado</MenuItem>

              <MenuItem value="ARQUIVADO">Arquivado</MenuItem>
            </TextField>

            {/* LIMPAR */}
            <Button
              size="small"
              variant="outlined"
              type="button"
              onClick={() => {
                setFiltroTexto("");
                setFiltroStatus("TODOS");
                setFiltroTipo("TODOS");

                setFiltrosColuna({
                  DOCUMENTO: "",
                  TIPO: [],
                  MODALIDADE: [],
                  STATUS: [],
                  ASSINANTES: "",
                  DOCUMENTOS: "",
                  CRIADO_EM_DE: "",
                  CRIADO_EM_ATE: "",
                });

                setFiltroColunaAberto(null);
                setAncoraFiltroColuna(null);

                setPagina(0);
              }}
              sx={{
                flexShrink: 0,
                width: "auto",
                height: 40,
                color: "#2196f3",
                borderColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                whiteSpace: "nowrap",

                "&:hover": {
                  borderColor: "#42a5f5",
                  backgroundColor: "rgba(33, 150, 243, 0.08)",
                },
              }}
            >
              Limpar
            </Button>

            {/* COPIAR */}
            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={handleCopiar}
              sx={{
                flexShrink: 0,
                width: "auto",
                height: 40,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                whiteSpace: "nowrap",

                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              <i
                className="fas fa-copy"
                style={{
                  marginRight: 6,
                }}
              />
              Copiar
            </Button>

            {/* CSV */}
            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={handleCSV}
              sx={{
                flexShrink: 0,
                width: "auto",
                height: 40,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                whiteSpace: "nowrap",

                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              CSV
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: 1,
          }}
        >
          {!gridMinimizado ? (
            <button
              type="button"
              onClick={() => setGridMinimizado(true)}
              style={{
                border: "1px solid #d32f2f",
                color: "#ff4c4c",
                background: "transparent",
                padding: "3px 10px",
                borderRadius: 5,
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 600,
                lineHeight: "18px",
              }}
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setGridMinimizado(false)}
              style={{
                border: "1px solid #1998a8",
                color: "#1998a8",
                background: "transparent",
                padding: "3px 10px",
                borderRadius: 5,
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 600,
                lineHeight: "18px",
              }}
            >
              ↗ Expandir
            </button>
          )}
        </Box>

        <Collapse in={!gridMinimizado}>
          <TableContainer
            component={Paper}
            sx={{
              mb: 3,
              backgroundColor: "transparent",
              boxShadow: "none",
            }}
          >
            {carregando ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <Table
                size="small"
                sx={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.95rem",
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                        fontWeight: "bold",
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Ações
                    </TableCell>

                    <TableCell
                      sx={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                        fontWeight: "bold",
                        borderBottom: "1px solid var(--ig-b-444444)",
                      }}
                    >
                      {cabecalhoFiltravel("Documento", "DOCUMENTO")}
                    </TableCell>

                    <TableCell
                      sx={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                        fontWeight: "bold",
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cabecalhoFiltravel("Tipo", "TIPO")}
                    </TableCell>

                    <TableCell
                      sx={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                        fontWeight: "bold",
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cabecalhoFiltravel("Modalidade", "MODALIDADE")}
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                        fontWeight: "bold",
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cabecalhoFiltravel("Status externo", "STATUS")}
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                        fontWeight: "bold",
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cabecalhoFiltravel("Assinantes", "ASSINANTES")}
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                        fontWeight: "bold",
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cabecalhoFiltravel("Documentos", "DOCUMENTOS")}
                    </TableCell>

                    <TableCell
                      sx={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                        fontWeight: "bold",
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cabecalhoFiltravel("Criado em", "CRIADO_EM")}
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {envelopesPaginados.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        align="center"
                        sx={{
                          color: "var(--ig-t-888888)",
                          py: 3,
                          borderBottom: "1px solid var(--ig-b-444444)",
                        }}
                      >
                        Nenhum envio para assinatura encontrado.
                      </TableCell>
                    </TableRow>
                  ) : (
                    envelopesPaginados.map((envelope) => (
                      <TableRow
                        key={envelope.id}
                        sx={{
                          "&:hover": {
                            backgroundColor: "var(--ig-f-2a2a2a)",
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            borderBottom: "1px solid var(--ig-b-444444)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Tooltip title="Visualizar detalhes">
                            <IconButton
                              size="small"
                              onClick={() => handleVisualizar(envelope)}
                              sx={{
                                backgroundColor: "#1998a8",
                                color: "#fff",
                                borderRadius: "4px",
                                mx: 0.25,
                                width: "auto",
                                "&:hover": { backgroundColor: "#1565c0" },
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title={enviandoId === envelope.id ? "Enviando..." : "Enviar para assinatura externa"}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={enviandoId === envelope.id}
                                onClick={() => handleEnviar(envelope)}

                                sx={{
                                  backgroundColor: "#2e7d32",
                                  color: "#fff",
                                  borderRadius: "4px",
                                  mx: 0.25,
                                  width: "auto",
                                  "&:hover": { backgroundColor: "#1b5e20" },
                                }}
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          {["FISICA", "FISICO"].includes(String(modalidadeProcesso(envelope) || "").toUpperCase()) && (
                            <Tooltip title="Arquivar assinatura física">
                              <IconButton
                                size="small"
                                onClick={() => handleArquivar(envelope)}
                                sx={{
                                  backgroundColor: "#6d4c41",
                                  color: "#fff",
                                  borderRadius: "4px",
                                  mx: 0.25,
                                  width: "auto",

                                  "&:hover": {
                                    backgroundColor: "#5d4037",
                                  },
                                }}
                              >
                                <ArchiveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: "1px solid var(--ig-b-444444)",
                            color: "var(--ig-t-ffffff)",
                          }}
                        >
                          {envelope.tituloContrato || "—"}
                        </TableCell>

                        <TableCell
                          sx={{
                            borderBottom: "1px solid var(--ig-b-444444)",
                            color: "var(--ig-t-ffffff)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {envelope.origemModulo === "PROPOSTA"
                            ? "Proposta Comercial"
                            : envelope.origemModulo === "CONTRATO"
                              ? "Contrato"
                              : tipoDocumentoLabel(envelope.origemModulo)}
                        </TableCell>

                        <TableCell
                          sx={{
                            borderBottom: "1px solid var(--ig-b-444444)",
                            color: "var(--ig-t-ffffff)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {modalidadeLabel(envelope.modalidadeAssinatura)}
                        </TableCell>

                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-ffffff)" }}>
                          <Chip
                            size="small"
                            label={statusLabel(envelope.statusFluxo || envelope.status)}
                            color={statusColor(envelope.statusFluxo || envelope.status)}
                          />
                        </TableCell>

                        <TableCell align="center" sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-ffffff)" }}>
                          {envelope.totalAssinantes ?? 0}
                        </TableCell>

                        <TableCell align="center" sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-ffffff)" }}>
                          {envelope.totalDocumentos ?? 0}
                        </TableCell>

                        <TableCell
                          sx={{
                            borderBottom: "1px solid var(--ig-b-444444)",
                            color: "var(--ig-t-cccccc)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {envelope.criadoEm ? new Date(envelope.criadoEm).toLocaleString("pt-BR") : "—"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}

            {!carregando && (
              <TablePagination
                component="div"
                count={envelopesOrdenados.length}
                page={pagina}
                onPageChange={(_, p) => setPagina(p)}
                rowsPerPage={porPagina}
                onRowsPerPageChange={(e) => {
                  setPorPagina(parseInt(e.target.value, 10));
                  setPagina(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage="Envios por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                sx={{
                  fontSize: "0.8rem",
                  minHeight: 42,
                  "& .MuiTablePagination-toolbar": {
                    minHeight: 42,
                  },
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                    fontSize: "0.8rem",
                  },
                  color: "var(--ig-t-ffffff)",
                  borderTop: "1px solid var(--ig-b-444444)",
                  "& .MuiTablePagination-selectIcon": { color: "var(--ig-t-ffffff)" },
                  "& .MuiTablePagination-actions button": {
                    color: "var(--ig-t-ffffff)",
                    width: "auto",
                  },
                  "& .MuiTablePagination-actions": {
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              />
            )}
          </TableContainer>
        </Collapse>
      </Box>

      {formNovaAssinaturaAberto && (
        <Box
          sx={{
            mt: 2,
            mb: 3,
            p: 2,
            border: "1px solid var(--ig-b-2b2b2b)",
            borderRadius: 1,
            backgroundColor: "transparent",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
            }}
          >
            Nova Assinatura Final
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <TextField
              select
              label="Tipo de Documento"
              value={novoProcesso.tipoDocumentoOrigem}
              onChange={(e) =>
                setNovoProcesso((prev) => ({
                  ...prev,
                  tipoDocumentoOrigem: e.target.value,
                  referenciaOrigemId: "",
                  tituloContrato: "",
                }))
              }
              fullWidth
            >
              <MenuItem value="PROPOSTA">Proposta Comercial</MenuItem>

              <MenuItem value="CONTRATO">Contrato</MenuItem>
            </TextField>

            <TextField
              label="Documento Aprovado"
              value={documentoAprovadoSelecionado?.titulo || ""}
              placeholder="Selecione um documento aprovado"
              onClick={handleAbrirSelecionarDocumentoAprovado}
              disabled={carregandoDocumentosAprovados}
              InputProps={{
                readOnly: true,
              }}
              helperText={
                carregandoDocumentosAprovados
                  ? "Carregando documentos aprovados..."
                  : documentosDisponiveis.length === 0
                    ? "Nenhum documento aprovado disponível."
                    : ""
              }
              fullWidth
              multiline
              minRows={1}
              maxRows={4}
              sx={{
                width: "100%",

                "& .MuiInputBase-root": {
                  cursor: "pointer",
                  alignItems: "flex-start",
                },

                "& .MuiInputBase-inputMultiline": {
                  cursor: "pointer",
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                  lineHeight: 1.4,
                },
              }}
            />

            <TextField
              label="Modalidade"
              select
              value={novoProcesso.modalidadeAssinatura}
              onChange={(e) => {
                const modalidade = e.target.value;

                setNovoProcesso((prev) => ({
                  ...prev,
                  modalidadeAssinatura: modalidade,
                }));

                setArquivoDocusignTeste(null);
              }}
              fullWidth
            >
              {novoProcesso.tipoDocumentoOrigem !== "PROPOSTA" && <MenuItem value="FISICA">Física — PDF + QR Code</MenuItem>}
              <MenuItem value="FISICA">Física — PDF + QR Code</MenuItem>

              <MenuItem value="DOCUSIGN">Digital — DocuSign</MenuItem>
            </TextField>

            {novoProcesso.modalidadeAssinatura === "DOCUSIGN" && (
              <Box
                sx={{
                  border: "1px dashed var(--ig-b-666666)",
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Documento para envio ao DocuSign
                </Typography>

                {gerandoDocumento ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">Gerando documento...</Typography>
                  </Box>
                ) : arquivoDocusignTeste ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="body2" sx={{ color: "success.light" }}>
                      {arquivoDocusignTeste.name}
                    </Typography>

                    <Box sx={{ display: "flex" }}>
                      <Tooltip title="Visualizar">
                        <IconButton
                          size="small"
                          onClick={abrirDocumentoGerado}
                          sx={{
                            backgroundColor: "#1998a8",
                            color: "#fff",
                            borderRadius: "4px",
                            mx: 0.25,
                            width: "auto",
                            "&:hover": { backgroundColor: "#1565c0" },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Baixar">
                        <IconButton
                          size="small"
                          onClick={baixarDocumentoGerado}
                          sx={{
                            backgroundColor: "#555",
                            color: "#fff",
                            borderRadius: "4px",
                            mx: 0.25,
                            width: "auto",
                            "&:hover": { backgroundColor: "#444" },
                          }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Selecione o documento aprovado acima para gerar o PDF automaticamente.
                  </Typography>
                )}
              </Box>
            )}

            {novoProcesso.modalidadeAssinatura === "FISICA" && (
              <Box
                sx={{
                  border: "1px dashed var(--ig-b-666666)",
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Documento assinado fisicamente (upload do computador)
                </Typography>

                {arquivoDocusignTeste ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="body2" sx={{ color: "success.light" }}>
                      {arquivoDocusignTeste.name}
                    </Typography>

                    <Box sx={{ display: "flex" }}>
                      <Tooltip title="Visualizar">
                        <IconButton
                          size="small"
                          onClick={abrirDocumentoGerado}
                          sx={{
                            backgroundColor: "#1998a8",
                            color: "#fff",
                            borderRadius: "4px",
                            mx: 0.25,
                            width: "auto",
                            "&:hover": { backgroundColor: "#1565c0" },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Baixar">
                        <IconButton
                          size="small"
                          onClick={baixarDocumentoGerado}
                          sx={{
                            backgroundColor: "#555",
                            color: "#fff",
                            borderRadius: "4px",
                            mx: 0.25,
                            width: "auto",
                            "&:hover": { backgroundColor: "#444" },
                          }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={excluirDocumentoGerado}
                          sx={{
                            backgroundColor: "#d32f2f",
                            color: "#fff",
                            borderRadius: "4px",
                            mx: 0.25,
                            width: "auto",
                            "&:hover": { backgroundColor: "#c62828" },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ) : (
                  <input type="file" accept=".pdf" onChange={(e) => setArquivoDocusignTeste(e.target.files?.[0] || null)} />
                )}
              </Box>
            )}
            <Box
              sx={{
                border: "1px solid var(--ig-b-444444)",
                borderRadius: 1,
                p: 2,
                mt: 2,
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                {novoProcesso.tipoDocumentoOrigem === "CONTRATO" ? "Partes do Contrato" : "Partes da Proposta Comercial"}
              </Typography>

              {!novoProcesso.referenciaOrigemId ? (
                <Alert severity="info">
                  {novoProcesso.tipoDocumentoOrigem === "CONTRATO"
                    ? "Selecione um contrato aprovado para carregar as partes."
                    : "Selecione uma proposta comercial aprovada para carregar as partes."}
                </Alert>
              ) : carregandoPartesContrato ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CircularProgress size={18} />

                  <Typography variant="body2">Carregando partes para assinatura...</Typography>
                </Box>
              ) : (
                <>
                  {partesContrato.locatarios.length === 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Nenhum locatário foi encontrado.
                    </Alert>
                  )}

                  {partesContrato.locatarios.map((pessoa, index) => (
                    <Box
                      key={`LOCATARIO-${pessoa.fichaId}`}
                      sx={{
                        borderTop: index === 0 ? "none" : "1px solid var(--ig-b-444444)",
                        pt: index === 0 ? 0 : 2,
                        mt: index === 0 ? 0 : 2,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1,
                          fontWeight: 600,
                        }}
                      >
                        Locatário
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "flex-start",
                        }}
                      >
                        <TextField
                          label="Ordem das Assinaturas"
                          type="number"
                          value={pessoa.ordemAssinatura ?? 1}
                          onChange={(e) => atualizarOrdemParte("locatarios", pessoa.fichaId, limitarOrdemAssinatura(e.target.value))}
                          inputProps={{
                            min: 1,
                            max: totalAssinantesNovaAssinatura,
                          }}
                          sx={{
                            width: 150,
                            flexShrink: 0,
                          }}
                        />

                        <TextField
                          label="Nome"
                          value={pessoa.nome || ""}
                          disabled
                          sx={{
                            flex: 1,
                          }}
                        />
                      </Box>

                      <TextField
                        label="E-mail para Assinatura"
                        type="email"
                        value={pessoa.email || ""}
                        onChange={(e) => atualizarEmailParte("locatarios", pessoa.fichaId, e.target.value)}
                        fullWidth
                        sx={{ mt: 1 }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          mt: 1,
                          alignItems: "flex-start",
                        }}
                      >
                        <TextField
                          label="Signatário"
                          value={pessoa.signatario || ""}
                          onChange={(e) => atualizarSignatarioParte("locatarios", pessoa.fichaId, e.target.value.toUpperCase())}
                          sx={{
                            flex: 1,
                            minWidth: 220,
                          }}
                        />

                        <TextField
                          select
                          label="Ciência"
                          value={pessoa.ciencia || "NÃO SE APLICA"}
                          onChange={(e) => atualizarCienciaParte("locatarios", pessoa.fichaId, e.target.value)}
                          sx={{
                            width: 240,
                            minWidth: 240,
                            flexShrink: 0,
                          }}
                        >
                          {OPCOES_CIENCIA.map((opcao) => (
                            <MenuItem key={opcao} value={opcao}>
                              {opcao}
                            </MenuItem>
                          ))}
                        </TextField>

                        {pessoa.ciencia === "SE APLICA" && (
                          <TextField
                            label="Informe a Ciência"
                            value={pessoa.descricaoCiencia || ""}
                            onChange={(e) => atualizarDescricaoCienciaParte("locatarios", pessoa.fichaId, e.target.value.toUpperCase())}
                            sx={{
                              flex: 1.2,
                              minWidth: 320,
                            }}
                          />
                        )}
                      </Box>
                      {pessoa.conjuge && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            border: "1px solid var(--ig-b-555555)",
                            borderRadius: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              mb: 1,
                              fontWeight: 600,
                            }}
                          >
                            Cônjuge do Locatário
                          </Typography>

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={Boolean(conjugesSelecionados[pessoa.fichaId])}
                                onChange={(e) =>
                                  setConjugesSelecionados((prev) => ({
                                    ...prev,
                                    [pessoa.fichaId]: e.target.checked,
                                  }))
                                }
                              />
                            }
                            label="Incluir cônjuge como assinante"
                          />

                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "flex-start",
                            }}
                          >
                            <TextField
                              label="Ordem das Assinaturas"
                              type="number"
                              value={pessoa.conjuge.ordemAssinatura ?? 1}
                              onChange={(e) => atualizarOrdemConjuge("locatarios", pessoa.fichaId, limitarOrdemAssinatura(e.target.value))}
                              inputProps={{
                                min: 1,
                                max: totalAssinantesNovaAssinatura,
                              }}
                              sx={{
                                width: 150,
                                flexShrink: 0,
                              }}
                            />

                            <TextField
                              label="Nome"
                              value={pessoa.conjuge.nome || ""}
                              disabled
                              sx={{
                                flex: 1,
                              }}
                            />
                          </Box>

                          <TextField
                            label="E-mail para Assinatura"
                            type="email"
                            value={pessoa.conjuge.email || ""}
                            onChange={(e) => atualizarEmailConjuge("locatarios", pessoa.fichaId, e.target.value)}
                            fullWidth
                            sx={{ mt: 1 }}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              mt: 1,
                              alignItems: "flex-start",
                            }}
                          >
                            <TextField
                              label="Signatário"
                              value={pessoa.conjuge.signatario || ""}
                              onChange={(e) => atualizarSignatarioConjuge("locatarios", pessoa.fichaId, e.target.value.toUpperCase())}
                              sx={{
                                flex: 1,
                                minWidth: 220,
                              }}
                            />

                            <TextField
                              select
                              label="Ciência"
                              value={pessoa.conjuge.ciencia || "NÃO SE APLICA"}
                              onChange={(e) => atualizarCienciaConjuge("locatarios", pessoa.fichaId, e.target.value)}
                              sx={{
                                width: 240,
                                minWidth: 240,
                                flexShrink: 0,
                              }}
                            >
                              {OPCOES_CIENCIA.map((opcao) => (
                                <MenuItem key={opcao} value={opcao}>
                                  {opcao}
                                </MenuItem>
                              ))}
                            </TextField>

                            {pessoa.conjuge.ciencia === "SE APLICA" && (
                              <TextField
                                label="Informe a Ciência"
                                value={pessoa.conjuge.descricaoCiencia || ""}
                                onChange={(e) => atualizarDescricaoCienciaConjuge("locatarios", pessoa.fichaId, e.target.value.toUpperCase())}
                                sx={{
                                  flex: 1.2,
                                  minWidth: 320,
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ))}

                  {partesContrato.fiadores.map((pessoa) => (
                    <Box
                      key={`FIADOR-${pessoa.fichaId}`}
                      sx={{
                        borderTop: "1px solid var(--ig-b-444444)",
                        pt: 2,
                        mt: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1,
                          fontWeight: 600,
                        }}
                      >
                        Fiador
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "flex-start",
                        }}
                      >
                        <TextField
                          label="Ordem das Assinaturas"
                          type="number"
                          value={pessoa.ordemAssinatura ?? 1}
                          onChange={(e) => atualizarOrdemParte("fiadores", pessoa.fichaId, limitarOrdemAssinatura(e.target.value))}
                          inputProps={{
                            min: 1,
                            max: totalAssinantesNovaAssinatura,
                          }}
                          sx={{
                            width: 150,
                            flexShrink: 0,
                          }}
                        />

                        <TextField
                          label="Nome"
                          value={pessoa.nome || ""}
                          disabled
                          sx={{
                            flex: 1,
                          }}
                        />
                      </Box>
                      <TextField
                        label="E-mail para Assinatura"
                        type="email"
                        value={pessoa.email || ""}
                        onChange={(e) => atualizarEmailParte("fiadores", pessoa.fichaId, e.target.value)}
                        fullWidth
                        sx={{ mt: 1 }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          mt: 1,
                          alignItems: "flex-start",
                        }}
                      >
                        <TextField
                          select
                          label="Signatário"
                          value={pessoa.signatario || "FIADOR"}
                          onChange={(e) => atualizarSignatarioParte("fiadores", pessoa.fichaId, e.target.value)}
                          sx={{
                            flex: 1,
                            minWidth: 220,
                          }}
                        >
                          {OPCOES_SIGNATARIO.map((opcao) => (
                            <MenuItem key={opcao} value={opcao}>
                              {opcao}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          select
                          label="Ciência"
                          value={pessoa.ciencia || "NÃO SE APLICA"}
                          onChange={(e) => atualizarCienciaParte("fiadores", pessoa.fichaId, e.target.value)}
                          sx={{
                            width: 240,
                            minWidth: 240,
                            flexShrink: 0,
                          }}
                        >
                          {OPCOES_CIENCIA.map((opcao) => (
                            <MenuItem key={opcao} value={opcao}>
                              {opcao}
                            </MenuItem>
                          ))}
                        </TextField>

                        {pessoa.ciencia === "SE APLICA" && (
                          <TextField
                            label="Informe a Ciência"
                            value={pessoa.descricaoCiencia || ""}
                            onChange={(e) => atualizarDescricaoCienciaParte("fiadores", pessoa.fichaId, e.target.value.toUpperCase())}
                            sx={{
                              flex: 1.2,
                              minWidth: 320,
                            }}
                          />
                        )}
                      </Box>
                      {pessoa.conjuge && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            border: "1px solid var(--ig-b-555555)",
                            borderRadius: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              mb: 1,
                              fontWeight: 600,
                            }}
                          >
                            Cônjuge do Fiador
                          </Typography>

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={Boolean(conjugesSelecionados[pessoa.fichaId])}
                                onChange={(e) =>
                                  setConjugesSelecionados((prev) => ({
                                    ...prev,
                                    [pessoa.fichaId]: e.target.checked,
                                  }))
                                }
                              />
                            }
                            label="Incluir cônjuge como assinante"
                          />

                          <TextField
                            label="Nome"
                            value={pessoa.conjuge.nome || ""}
                            InputProps={{
                              readOnly: true,
                            }}
                            fullWidth
                          />

                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              mt: 1,
                              alignItems: "flex-start",
                            }}
                          >
                            <TextField
                              label="E-mail para Assinatura"
                              type="email"
                              value={pessoa.conjuge.email || ""}
                              onChange={(e) => atualizarEmailConjuge("fiadores", pessoa.fichaId, e.target.value)}
                              sx={{
                                flex: 1,
                              }}
                            />

                            <TextField
                              label="Ordem de Assinatura"
                              type="number"
                              value={pessoa.conjuge.ordemAssinatura ?? 1}
                              onChange={(e) => atualizarOrdemConjuge("fiadores", pessoa.fichaId, Math.max(1, Number(e.target.value) || 1))}
                              inputProps={{
                                min: 1,
                              }}
                              sx={{
                                width: 150,
                                flexShrink: 0,
                              }}
                            />
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              mt: 1,
                              alignItems: "flex-start",
                            }}
                          >
                            <TextField
                              select
                              label="Signatário"
                              value={pessoa.conjuge.signatario || "CÔNJUGE DO FIADOR"}
                              onChange={(e) => atualizarSignatarioConjuge("fiadores", pessoa.fichaId, e.target.value)}
                              sx={{
                                flex: 1,
                                minWidth: 220,
                              }}
                            >
                              {OPCOES_SIGNATARIO.map((opcao) => (
                                <MenuItem key={opcao} value={opcao}>
                                  {opcao}
                                </MenuItem>
                              ))}
                            </TextField>

                            <TextField
                              select
                              label="Ciência"
                              value={pessoa.conjuge.ciencia || "NÃO SE APLICA"}
                              onChange={(e) => atualizarCienciaConjuge("fiadores", pessoa.fichaId, e.target.value)}
                              sx={{
                                width: 240,
                                minWidth: 240,
                                flexShrink: 0,
                              }}
                            >
                              {OPCOES_CIENCIA.map((opcao) => (
                                <MenuItem key={opcao} value={opcao}>
                                  {opcao}
                                </MenuItem>
                              ))}
                            </TextField>

                            {pessoa.conjuge.ciencia === "SE APLICA" && (
                              <TextField
                                label="Informe a Ciência"
                                value={pessoa.conjuge.descricaoCiencia || ""}
                                onChange={(e) => atualizarDescricaoCienciaConjuge("fiadores", pessoa.fichaId, e.target.value.toUpperCase())}
                                sx={{
                                  flex: 1.2,
                                  minWidth: 320,
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ))}
                </>
              )}
            </Box>

            <Box
              sx={{
                border: "1px solid var(--ig-b-444444)",
                borderRadius: 1,
                p: 2,
                mt: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 1.5,
                  fontWeight: 600,
                }}
              >
                Assinantes da Empresa — Alçada de Assinaturas
              </Typography>

              {carregandoAlcadaAssinatura ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CircularProgress size={18} />

                  <Typography variant="body2">Carregando assinantes da empresa...</Typography>
                </Box>
              ) : !alcadaAssinaturaContrato ? (
                <Alert severity="warning">
                  Nenhuma Alçada de Assinaturas foi encontrada para {novoProcesso.tipoDocumentoOrigem === "CONTRATO" ? "CONTRATO" : "PROPOSTA COMERCIAL"}.
                </Alert>
              ) : (alcadaAssinaturaContrato.assinantes ?? []).length === 0 ? (
                <Alert severity="warning">A Alçada de Assinaturas não possui assinantes ativos.</Alert>
              ) : (
                [...(alcadaAssinaturaContrato.assinantes ?? [])]
                  .sort((a, b) => Number(a.ordem) - Number(b.ordem))
                  .map((assinante) => (
                    <Box
                      key={assinante.id}
                      sx={{
                        borderTop: "1px solid var(--ig-b-444444)",
                        pt: 2,
                        mt: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1,
                          fontWeight: 600,
                        }}
                      >
                        {assinante.identificacaoExibicao || "Assinante da Empresa"}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "flex-start",
                        }}
                      >
                        <TextField
                          label="Ordem das Assinaturas"
                          type="number"
                          value={assinante.ordemAssinatura ?? 1}
                          onChange={(e) => atualizarOrdemAssinanteEmpresa(assinante.id, limitarOrdemAssinatura(e.target.value))}
                          inputProps={{
                            min: 1,
                            max: totalAssinantesNovaAssinatura,
                          }}
                          sx={{
                            width: 150,
                            flexShrink: 0,
                          }}
                        />

                        <TextField
                          label="Nome"
                          value={assinante.usuario?.nome || ""}
                          disabled
                          sx={{
                            flex: 1,
                          }}
                        />
                      </Box>

                      <TextField
                        label="E-mail para Assinatura"
                        type="email"
                        value={assinante.email || ""}
                        onChange={(e) => atualizarEmailAssinanteEmpresa(assinante.id, e.target.value)}
                        fullWidth
                        sx={{ mt: 1 }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          mt: 1,
                          alignItems: "flex-start",
                        }}
                      >
                        <TextField
                          select
                          label="Signatário"
                          value={assinante.signatario || assinante.identificacao || "REPRESENTANTE LEGAL"}
                          onChange={(e) => atualizarSignatarioAssinanteEmpresa(assinante.id, e.target.value)}
                          sx={{
                            flex: 1,
                            minWidth: 220,
                          }}
                        >
                          {OPCOES_SIGNATARIO.map((opcao) => (
                            <MenuItem key={opcao} value={opcao}>
                              {opcao}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          select
                          label="Ciência"
                          value={assinante.ciencia || "NÃO SE APLICA"}
                          onChange={(e) => atualizarCienciaAssinanteEmpresa(assinante.id, e.target.value)}
                          sx={{
                            width: 240,
                            minWidth: 240,
                            flexShrink: 0,
                          }}
                        >
                          {OPCOES_CIENCIA.map((opcao) => (
                            <MenuItem key={opcao} value={opcao}>
                              {opcao}
                            </MenuItem>
                          ))}
                        </TextField>

                        {assinante.ciencia === "SE APLICA" && (
                          <TextField
                            label="Informe a Ciência"
                            value={assinante.descricaoCiencia || ""}
                            onChange={(e) => atualizarDescricaoCienciaAssinanteEmpresa(assinante.id, e.target.value.toUpperCase())}
                            sx={{
                              flex: 1.2,
                              minWidth: 320,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  ))
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
              mt: 2,
            }}
          >
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => {
                setFormNovaAssinaturaAberto(false);

                setNovoProcesso({
                  tipoDocumentoOrigem: "PROPOSTA",
                  referenciaOrigemId: "",
                  tituloContrato: "",
                  modalidadeAssinatura: "DOCUSIGN",
                  observacoes: "",
                });

                setAlcadaAssinaturaContrato(null);

                setPartesContrato({
                  locatarios: [],
                  fiadores: [],
                });

                setConjugesSelecionados({});

                setArquivoDocusignTeste(null);

                setModalSelecionarDocumentoAprovado(false);
                setBuscaDocumentoAprovado("");
                setPaginaDocumentoAprovado(0);
              }}
              sx={{
                width: "auto",
                minWidth: 120,
              }}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              color="success"
              onClick={handleSalvarNovaAssinatura}
              disabled={gerandoDocumento || carregandoPartesContrato || carregandoAlcadaAssinatura}
              sx={{
                width: "auto",
                minWidth: 120,
              }}
            >
              Salvar
            </Button>
          </Box>
        </Box>
      )}

      <Dialog open={modalSelecionarDocumentoAprovado} onClose={handleFecharSelecionarDocumentoAprovado} fullWidth maxWidth="md">
        <DialogTitle>{novoProcesso.tipoDocumentoOrigem === "CONTRATO" ? "Selecionar Contrato" : "Selecionar Proposta"}</DialogTitle>

        <DialogContent>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mt: 1,
              mb: 2,
              alignItems: "flex-start",
            }}
          >
            <TextField
              placeholder={novoProcesso.tipoDocumentoOrigem === "CONTRATO" ? "Título do contrato..." : "Código, apelido, complemento ou locatário..."}
              value={buscaDocumentoAprovado}
              onChange={(e) => {
                setBuscaDocumentoAprovado(e.target.value);
                setPaginaDocumentoAprovado(0);
              }}
              fullWidth
            />

            <Button
              variant="contained"
              onClick={() => setPaginaDocumentoAprovado(0)}
              sx={{
                width: "auto",
                minWidth: 120,
                height: 56,
                flexShrink: 0,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Pesquisar
            </Button>
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              backgroundColor: "transparent",
              boxShadow: "none",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: "var(--ig-f-1e1e1e)",
                      color: "var(--ig-t-ffffff)",
                      fontWeight: "bold",
                      borderBottom: "1px solid var(--ig-b-444444)",
                    }}
                  >
                    Documento Aprovado
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {documentosDisponiveisPaginados.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={1}
                      align="center"
                      sx={{
                        color: "var(--ig-t-888888)",
                        py: 3,
                        borderBottom: "1px solid var(--ig-b-444444)",
                      }}
                    >
                      Nenhum documento aprovado encontrado.
                    </TableCell>
                  </TableRow>
                ) : (
                  documentosDisponiveisPaginados.map((item) => (
                    <TableRow
                      key={item.id}
                      hover
                      onClick={() => handleSelecionarDocumentoAprovado(item)}
                      sx={{
                        cursor: "pointer",

                        "&:hover": {
                          backgroundColor: "var(--ig-f-2a2a2a)",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          borderBottom: "1px solid var(--ig-b-444444)",
                          color: "var(--ig-t-ffffff)",
                          py: 1.2,
                        }}
                      >
                        {item.titulo}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={documentosDisponiveisFiltrados.length}
            page={paginaDocumentoAprovado}
            onPageChange={(_, novaPagina) => setPaginaDocumentoAprovado(novaPagina)}
            rowsPerPage={porPaginaDocumentoAprovado}
            onRowsPerPageChange={(e) => {
              setPorPaginaDocumentoAprovado(parseInt(e.target.value, 10));
              setPaginaDocumentoAprovado(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="Por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            sx={{
              fontSize: "0.8rem",
              minHeight: 42,
              "& .MuiTablePagination-toolbar": {
                minHeight: 42,
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "0.8rem",
              },
              color: "var(--ig-t-ffffff)",
              borderTop: "1px solid var(--ig-b-444444)",
              "& .MuiTablePagination-selectIcon": { color: "var(--ig-t-ffffff)" },
              "& .MuiTablePagination-actions button": {
                color: "var(--ig-t-ffffff)",
                width: "auto",
              },
              "& .MuiTablePagination-actions": {
                display: "flex",
                alignItems: "center",
              },
            }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleFecharSelecionarDocumentoAprovado}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {modalDetalhes && assinaturaSelecionada && (
        <Box
          sx={{
            mt: 3,
            mb: 3,
            p: 2,
            border: "1px solid var(--ig-b-2b2b2b)",
            borderRadius: 1,
            backgroundColor: "transparent",
            width: "100%",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
            }}
          >
            Detalhes da Assinatura
          </Typography>

          <Tabs
            value={abaDetalhes}
            onChange={(_, novaAba) => setAbaDetalhes(novaAba)}
            variant="fullWidth"
            centered
            sx={{
              borderBottom: "1px solid var(--ig-b-444444)",

              "& .MuiTab-root": {
                color: "var(--ig-t-cccccc)",
                fontWeight: 600,
                fontSize: "0.85rem",
                minHeight: 44,
                padding: "8px 12px",
                textTransform: "none",
              },

              "& .Mui-selected": {
                color: "#d32f2f !important",
              },

              "& .MuiTabs-indicator": {
                backgroundColor: "#d32f2f",
                height: 3,
              },
            }}
          >
            <Tab label="Detalhes" />
            <Tab label="Documentos" />
            <Tab label="Assinantes" />
            <Tab label="Histórico" />
          </Tabs>

          <Box sx={{ mt: 1 }}>
            {assinaturaSelecionada && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  mt: 1,
                }}
              >
                {abaDetalhes === 0 && (
                  <>
                    <TextField label="Documento" value={assinaturaSelecionada.tituloContrato || ""} disabled fullWidth />

                    <TextField label="Modalidade" value={modalidadeLabel(modalidadeProcesso(assinaturaSelecionada))} disabled fullWidth />

                    <TextField label="Status" value={statusLabel(assinaturaSelecionada.statusFluxo || assinaturaSelecionada.status)} disabled fullWidth />

                    <TextField label="Assinantes" value={assinaturaSelecionada.totalAssinantes ?? 0} disabled fullWidth />

                    <TextField label="Documentos" value={assinaturaSelecionada.totalDocumentos ?? 0} disabled fullWidth />

                    <TextField
                      label="Criado em"
                      value={assinaturaSelecionada.criadoEm ? new Date(assinaturaSelecionada.criadoEm).toLocaleString("pt-BR") : ""}
                      disabled
                      fullWidth
                    />
                    <TextField
                      label="Observações"
                      value={observacoesDetalhes}
                      onChange={(e) => setObservacoesDetalhes(e.target.value.toUpperCase())}
                      disabled={processoBloqueado || !editandoObservacoes}
                      fullWidth
                      multiline
                      minRows={3}
                    />

                    {!processoBloqueado && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        {!editandoObservacoes ? (
                          <Button size="small" variant="contained" color="primary" onClick={() => setEditandoObservacoes(true)}>
                            Editar Observação
                          </Button>
                        ) : (
                          <>
                            <Button size="small" variant="contained" color="success" onClick={handleSalvarObservacoes}>
                              Salvar Observação
                            </Button>

                            <Button
                              size="small"
                              variant="outlined"
                              color="inherit"
                              onClick={() => {
                                setEditandoObservacoes(false);
                                setObservacoesDetalhes(assinaturaSelecionada?.observacoes || "");
                              }}
                            >
                              Cancelar
                            </Button>
                          </>
                        )}
                      </Box>
                    )}

                    {["FISICA", "FISICO"].includes(modalidadeProcesso(assinaturaSelecionada)) && (
                      <>
                        <Typography variant="h6" sx={{ mt: 2 }}>
                          Assinatura Física
                        </Typography>

                        <TextField select label="Status da Assinatura" value={statusFisico} onChange={(e) => setStatusFisico(e.target.value)} fullWidth>
                          <MenuItem value="RASCUNHO">Rascunho</MenuItem>

                          <MenuItem value="EM_ASSINATURA_FISICA">Em Assinatura Física</MenuItem>

                          <MenuItem value="ASSINADO">Assinado</MenuItem>

                          <MenuItem value="ARQUIVADO">Arquivado</MenuItem>
                        </TextField>

                        <Button variant="contained" color="primary" onClick={handleSalvarStatusFisico}>
                          Salvar Status
                        </Button>

                        <Box>
                          <input type="file" accept=".pdf" onChange={(e) => setArquivoFisico(e.target.files?.[0] || null)} />
                        </Box>

                        <Button variant="contained" color="success" onClick={handleUploadFisico}>
                          Enviar Documento Assinado
                        </Button>
                      </>
                    )}
                  </>
                )}

                {abaDetalhes === 1 && (
                  <>
                    <Typography variant="h6" sx={{ mt: 3 }}>
                      Documentos
                    </Typography>

                    {documentosAssinatura.length === 0 ? (
                      <Typography color="text.secondary">Nenhum documento anexado.</Typography>
                    ) : (
                      documentosAssinatura.map((doc) => (
                        <Paper
                          key={doc.id}
                          sx={{
                            p: 2,
                            background: "var(--ig-f-2b2b2b)",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Box>
                            <Typography>{doc.nomeOriginal}</Typography>

                            <Typography variant="body2" color="text.secondary">
                              {tipoDocumentoLabel(doc.tipoDocumento)}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              flexShrink: 0,
                            }}
                          >
                            <Button size="small" variant="outlined" onClick={() => handleVisualizarDocumento(doc)}>
                              Visualizar
                            </Button>

                            <Button size="small" variant="contained" onClick={() => handleBaixarDocumento(doc)}>
                              Baixar
                            </Button>
                          </Box>
                        </Paper>
                      ))
                    )}
                  </>
                )}

                {abaDetalhes === 2 && (
                  <>
                    <Typography variant="h6" sx={{ mt: 3 }}>
                      Assinantes
                    </Typography>

                    {processoBloqueado && (
                      <Alert severity="warning">Este processo não permite alteração dos dados dos assinantes porque já foi enviado para assinatura.</Alert>
                    )}

                    {!processoBloqueado && (
                      <Box sx={{ mb: 2 }}>
                        {!editandoAssinantes ? (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              editandoAssinantesRef.current = true;
                              setEditandoAssinantes(true);
                            }}
                            sx={{
                              width: "auto",
                              minWidth: 180,
                            }}
                          >
                            Editar Assinantes
                          </Button>
                        ) : (
                          <>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                mb: 1,
                                alignItems: "center",
                                flexWrap: "wrap",
                              }}
                            >
                              <Button
                                variant="contained"
                                color="success"
                                onClick={handleSalvarAssinantes}
                                sx={{
                                  width: "auto",
                                  minWidth: 180,
                                }}
                              >
                                Salvar Assinantes
                              </Button>

                              <Button
                                variant="outlined"
                                color="inherit"
                                onClick={() => {
                                  editandoAssinantesRef.current = false;
                                  setEditandoAssinantes(false);

                                  handleVisualizar({
                                    id: assinaturaSelecionada.id,
                                  });
                                }}
                                sx={{
                                  width: "auto",
                                  minWidth: 120,
                                }}
                              >
                                Cancelar
                              </Button>
                            </Box>

                            <Alert
                              severity="info"
                              sx={{
                                mb: 2,
                              }}
                            >
                              Você pode alterar e-mail, signatário e ciência. Nome, ordem, papel e status são mantidos.
                            </Alert>
                          </>
                        )}
                      </Box>
                    )}

                    {participantesAssinatura.length === 0 ? (
                      <Typography color="text.secondary">Nenhum assinante registrado.</Typography>
                    ) : (
                      participantesAssinatura
                        .sort((a, b) => a.ordem - b.ordem)
                        .map((participante) => (
                          <Paper
                            key={participante.id}
                            sx={{
                              p: 2,
                              mb: 1,
                              background: "var(--ig-f-2b2b2b)",
                            }}
                          >
                            <Typography fontWeight={600}>
                              Assinante {participante.ordem}
                              {" - "}
                              {participantePapelLabel(participante.papel)}
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                mt: 1,
                                alignItems: "flex-start",
                              }}
                            >
                              <TextField
                                label="Ordem das Assinaturas"
                                value={participante.ordem ?? ""}
                                disabled
                                sx={{
                                  width: 180,
                                  flexShrink: 0,
                                }}
                              />

                              <TextField
                                label="Nome"
                                value={participante.nome || ""}
                                disabled
                                sx={{
                                  flex: 1,
                                }}
                              />
                            </Box>

                            <TextField
                              label="E-mail para Assinatura"
                              type="email"
                              value={participante.email || ""}
                              disabled={processoBloqueado || !editandoAssinantes}
                              onChange={(e) => {
                                setParticipantesAssinatura((prev) =>
                                  prev.map((p) =>
                                    p.id === participante.id
                                      ? {
                                          ...p,
                                          email: e.target.value,
                                        }
                                      : p,
                                  ),
                                );
                              }}
                              fullWidth
                              sx={{ mt: 1 }}
                            />

                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                mt: 1,
                                alignItems: "flex-start",
                                flexWrap: "wrap",
                              }}
                            >
                              <TextField
                                label="Signatário"
                                value={participante.signatario || ""}
                                disabled={processoBloqueado || !editandoAssinantes}
                                onChange={(e) => {
                                  const valor = e.target.value.toUpperCase();

                                  setParticipantesAssinatura((prev) =>
                                    prev.map((p) =>
                                      p.id === participante.id
                                        ? {
                                            ...p,
                                            signatario: valor,
                                          }
                                        : p,
                                    ),
                                  );
                                }}
                                sx={{
                                  flex: 1,
                                  minWidth: 220,
                                }}
                              />

                              <TextField
                                select
                                label="Ciência"
                                value={participante.ciencia || "NÃO SE APLICA"}
                                disabled={processoBloqueado || !editandoAssinantes}
                                onChange={(e) => {
                                  const novaCiencia = e.target.value;

                                  setParticipantesAssinatura((prev) =>
                                    prev.map((p) =>
                                      p.id === participante.id
                                        ? {
                                            ...p,
                                            ciencia: novaCiencia,
                                            descricaoCiencia: novaCiencia === "SE APLICA" ? p.descricaoCiencia || "" : "",
                                          }
                                        : p,
                                    ),
                                  );
                                }}
                                sx={{
                                  width: 240,
                                  minWidth: 240,
                                  flexShrink: 0,
                                }}
                              >
                                {OPCOES_CIENCIA.map((opcao) => (
                                  <MenuItem key={opcao} value={opcao}>
                                    {opcao}
                                  </MenuItem>
                                ))}
                              </TextField>

                              {String(participante.ciencia || "").toUpperCase() === "SE APLICA" && (
                                <TextField
                                  label="Informe a Ciência"
                                  value={participante.descricaoCiencia || ""}
                                  disabled={processoBloqueado || !editandoAssinantes}
                                  onChange={(e) => {
                                    const valor = e.target.value.toUpperCase();

                                    setParticipantesAssinatura((prev) =>
                                      prev.map((p) =>
                                        p.id === participante.id
                                          ? {
                                              ...p,
                                              descricaoCiencia: valor,
                                            }
                                          : p,
                                      ),
                                    );
                                  }}
                                  sx={{
                                    flex: 1.2,
                                    minWidth: 320,
                                  }}
                                />
                              )}
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                mt: 1,
                                fontWeight: 600,
                                color:
                                  participante.status === "ASSINADO"
                                    ? "success.main"
                                    : participante.status === "PENDENTE"
                                      ? "warning.main"
                                      : participante.status === "AGUARDANDO"
                                        ? "info.main"
                                        : participante.status === "RECUSADO"
                                          ? "error.main"
                                          : "text.secondary",
                              }}
                            >
                              Status: {participanteStatusLabel(participante.status)}
                            </Typography>

                            {participante.assinadoEm && (
                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 0.25,
                                  fontWeight: 600,
                                  color: "success.main",
                                }}
                              >
                                Assinado em: {new Date(participante.assinadoEm).toLocaleString("pt-BR")}
                              </Typography>
                            )}
                          </Paper>
                        ))
                    )}
                  </>
                )}

                {abaDetalhes === 3 && (
                  <>
                    <Typography variant="h6" sx={{ mt: 3 }}>
                      Histórico do Processo
                    </Typography>

                    {eventosAssinatura.length === 0 ? (
                      <Typography color="text.secondary">Nenhum evento registrado.</Typography>
                    ) : (
                      eventosAssinatura.map((evento) => (
                        <Paper
                          key={evento.id}
                          sx={{
                            p: 2,
                            mb: 1,
                            background: "var(--ig-f-2b2b2b)",
                          }}
                        >
                          <Typography fontWeight={600}>{eventoLabel(evento.tipoEvento)}</Typography>

                          <Typography>{descricaoEventoLabel(evento.descricao)}</Typography>

                          <Typography variant="caption" color="text.secondary">
                            {evento.criadoEm ? new Date(evento.criadoEm).toLocaleString("pt-BR") : ""}
                          </Typography>
                        </Paper>
                      ))
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setModalDetalhes(false);
                setAssinaturaSelecionada(null);
              }}
              sx={{
                width: "auto",
                minWidth: 100,
              }}
            >
              Fechar
            </Button>
          </Box>
        </Box>
      )}

      <Popover
        open={Boolean(filtroColunaAberto && ancoraFiltroColuna)}
        anchorEl={ancoraFiltroColuna}
        onClose={fecharFiltroColuna}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              width: 320,
              minWidth: 320,
              maxWidth: 320,
              backgroundColor: "var(--ig-f-202534)",
              border: "1px solid var(--ig-b-3d4658)",
              overflow: "visible",
            },
          },
        }}
      >
        {filtroColunaAberto === "DOCUMENTO" && (
          <Box>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 0.75,
                color: "var(--ig-t-cccccc)",
                fontWeight: 600,
              }}
            >
              Documento
            </Typography>

            <TextField
              autoFocus
              size="small"
              fullWidth
              value={filtrosColuna.DOCUMENTO}
              onChange={(event) => alterarFiltroColuna("DOCUMENTO", event.target.value)}
              placeholder="Pesquisar documento..."
              sx={{
                width: "100%",
                minWidth: 0,
              }}
            />
          </Box>
        )}

        {filtroColunaAberto === "TIPO" && (
          <Box>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 1,
                color: "var(--ig-t-cccccc)",
                fontWeight: 600,
              }}
            >
              Tipo
            </Typography>

            {["PROPOSTA COMERCIAL", "CONTRATO"].map((valor) => {
              const marcado = filtrosColuna.TIPO.includes(valor);

              return (
                <FormControlLabel
                  key={valor}
                  control={<Checkbox size="small" checked={marcado} onChange={() => alternarFiltroMultiplo("TIPO", valor)} />}
                  label={valor === "PROPOSTA COMERCIAL" ? "Proposta Comercial" : "Contrato"}
                  sx={{
                    display: "flex",
                    width: "100%",
                    m: 0,
                    px: 0.5,

                    "&:hover": {
                      backgroundColor: "#2a3140",
                    },
                  }}
                />
              );
            })}

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
                mt: 1.5,
                pt: 1,
                borderTop: "1px solid var(--ig-b-3d4658)",
              }}
            >
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setFiltrosColuna((prev) => ({
                    ...prev,
                    TIPO: [],
                  }));

                  setPagina(0);
                }}
                sx={{
                  width: "auto",
                  textTransform: "none",
                }}
              >
                Limpar
              </Button>

              <Button
                size="small"
                variant="contained"
                onClick={fecharFiltroColuna}
                sx={{
                  width: "auto",
                  textTransform: "none",
                }}
              >
                Aplicar
              </Button>
            </Box>
          </Box>
        )}

        {filtroColunaAberto === "MODALIDADE" && (
          <Box>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 1,
                color: "var(--ig-t-cccccc)",
                fontWeight: 600,
              }}
            >
              Modalidade
            </Typography>

            {[
              {
                value: "DIGITAL — DOCUSIGN",
                label: "Digital — DocuSign",
              },
              {
                value: "FÍSICO — PDF + QR CODE",
                label: "Físico — PDF + QR Code",
              },
            ].map((opcao) => {
              const marcado = filtrosColuna.MODALIDADE.includes(opcao.value);

              return (
                <FormControlLabel
                  key={opcao.value}
                  control={<Checkbox size="small" checked={marcado} onChange={() => alternarFiltroMultiplo("MODALIDADE", opcao.value)} />}
                  label={opcao.label}
                  sx={{
                    display: "flex",
                    width: "100%",
                    m: 0,
                    px: 0.5,

                    "&:hover": {
                      backgroundColor: "#2a3140",
                    },
                  }}
                />
              );
            })}

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
                mt: 1.5,
                pt: 1,
                borderTop: "1px solid var(--ig-b-3d4658)",
              }}
            >
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setFiltrosColuna((prev) => ({
                    ...prev,
                    MODALIDADE: [],
                  }));

                  setPagina(0);
                }}
                sx={{
                  width: "auto",
                  textTransform: "none",
                }}
              >
                Limpar
              </Button>

              <Button
                size="small"
                variant="contained"
                onClick={fecharFiltroColuna}
                sx={{
                  width: "auto",
                  textTransform: "none",
                }}
              >
                Aplicar
              </Button>
            </Box>
          </Box>
        )}

        {filtroColunaAberto === "STATUS" && (
          <Box>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 1,
                color: "var(--ig-t-cccccc)",
                fontWeight: 600,
              }}
            >
              Status externo
            </Typography>

            {[
              {
                value: "RASCUNHO",
                label: "Rascunho",
              },
              {
                value: "PRONTO PARA ENVIO",
                label: "Pronto para Envio",
              },
              {
                value: "ENVIADO",
                label: "Enviado",
              },
              {
                value: "AGUARDANDO ASSINATURAS",
                label: "Aguardando Assinaturas",
              },
              {
                value: "PARCIALMENTE ASSINADO",
                label: "Parcialmente Assinado",
              },
              {
                value: "ASSINADO",
                label: "Assinado",
              },
              {
                value: "ARQUIVADO",
                label: "Arquivado",
              },
              {
                value: "CANCELADO",
                label: "Cancelado",
              },
              {
                value: "EM ASSINATURA FÍSICA",
                label: "Em Assinatura Física",
              },
            ].map((opcao) => {
              const marcado = filtrosColuna.STATUS.includes(opcao.value);

              return (
                <FormControlLabel
                  key={opcao.value}
                  control={<Checkbox size="small" checked={marcado} onChange={() => alternarFiltroMultiplo("STATUS", opcao.value)} />}
                  label={opcao.label}
                  sx={{
                    display: "flex",
                    width: "100%",
                    m: 0,
                    px: 0.5,

                    "&:hover": {
                      backgroundColor: "#2a3140",
                    },
                  }}
                />
              );
            })}

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
                mt: 1.5,
                pt: 1,
                borderTop: "1px solid var(--ig-b-3d4658)",
              }}
            >
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setFiltrosColuna((prev) => ({
                    ...prev,
                    STATUS: [],
                  }));

                  setPagina(0);
                }}
                sx={{
                  width: "auto",
                  textTransform: "none",
                }}
              >
                Limpar
              </Button>

              <Button
                size="small"
                variant="contained"
                onClick={fecharFiltroColuna}
                sx={{
                  width: "auto",
                  textTransform: "none",
                }}
              >
                Aplicar
              </Button>
            </Box>
          </Box>
        )}

        {filtroColunaAberto === "ASSINANTES" && (
          <Box>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 0.75,
                color: "var(--ig-t-cccccc)",
                fontWeight: 600,
              }}
            >
              Quantidade de Assinantes
            </Typography>

            <TextField
              autoFocus
              size="small"
              fullWidth
              type="number"
              value={filtrosColuna.ASSINANTES}
              onChange={(event) => alterarFiltroColuna("ASSINANTES", event.target.value)}
              slotProps={{
                htmlInput: {
                  min: 0,
                },
              }}
            />
          </Box>
        )}

        {filtroColunaAberto === "DOCUMENTOS" && (
          <Box>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 0.75,
                color: "var(--ig-t-cccccc)",
                fontWeight: 600,
              }}
            >
              Quantidade de Documentos
            </Typography>

            <TextField
              autoFocus
              size="small"
              fullWidth
              type="number"
              value={filtrosColuna.DOCUMENTOS}
              onChange={(event) => alterarFiltroColuna("DOCUMENTOS", event.target.value)}
              slotProps={{
                htmlInput: {
                  min: 0,
                },
              }}
            />
          </Box>
        )}

        {filtroColunaAberto === "CRIADO_EM" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 0.75,
                  color: "var(--ig-t-cccccc)",
                  fontWeight: 600,
                }}
              >
                De
              </Typography>

              <TextField
                size="small"
                fullWidth
                type="date"
                value={filtrosColuna.CRIADO_EM_DE}
                onChange={(event) => {
                  setFiltrosColuna((prev) => ({
                    ...prev,
                    CRIADO_EM_DE: event.target.value,
                  }));

                  setPagina(0);
                }}
                sx={{
                  width: "100%",

                  "& input": {
                    color: "var(--ig-t-ffffff)",
                  },

                  "& input::-webkit-calendar-picker-indicator": {
                    filter: "var(--ig-x-11o99ka)",
                    cursor: "pointer",
                  },
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 0.75,
                  color: "var(--ig-t-cccccc)",
                  fontWeight: 600,
                }}
              >
                Até
              </Typography>

              <TextField
                size="small"
                fullWidth
                type="date"
                value={filtrosColuna.CRIADO_EM_ATE}
                onChange={(event) => {
                  setFiltrosColuna((prev) => ({
                    ...prev,
                    CRIADO_EM_ATE: event.target.value,
                  }));

                  setPagina(0);
                }}
                sx={{
                  width: "100%",

                  "& input": {
                    color: "var(--ig-t-ffffff)",
                  },

                  "& input::-webkit-calendar-picker-indicator": {
                    filter: "var(--ig-x-11o99ka)",
                    cursor: "pointer",
                  },
                }}
              />
            </Box>
          </Box>
        )}
      </Popover>
      <Snackbar
        open={snack.aberto}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, aberto: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, aberto: false }))} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}
